import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { db, auth } from '../firebase';
import {
  collection,
  getDocs,
  query,
  where,
  deleteDoc,
  doc,
  orderBy,
  serverTimestamp,
  addDoc,
  getDoc
} from 'firebase/firestore';
import { onSnapshot } from '../utils/firestoreListen';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import WaysOfSeeingModal from '../components/WaysOfSeeingModal';
import TransformationNavigationModal from '../components/TransformationNavigationModal';
import ReimagineScrollerCard from '../components/ReimagineScrollerCard';
import ReimagineFeedbackScrollerCard from '../components/ReimagineFeedbackScrollerCard';
import ReimaginedArtworkCard from '../components/ReimaginedArtworkCard';
import ArtAnalysisHotspots from '../components/ArtAnalysisHotspots';
import { FaTrash, FaEye, FaBook, FaGraduationCap, FaGlobe, FaEdit, FaCheckCircle, FaStar, FaExpandArrowsAlt, FaTimes, FaArrowLeft } from 'react-icons/fa';
import { Home, LogOut, ArrowLeft, LayoutDashboard, X, Eye, Palette, Wand2 } from 'lucide-react';
import { migrateActivityDescriptions, checkActivityDescriptions } from '../utils/migrateActivities';
import { motion, AnimatePresence } from 'framer-motion';
import { checkExistingActivity, checkTutorialById, cleanupTutorialActivities, getTransformedArtworksForOriginal, getTransformedArtworksByFeature, dedupeWaysOfSeeingActivitiesByArtworkStyle } from '../utils/activityService';
import StepByStepLearning from '../components/StepByStepLearning';
import ConsistentLoader from '../components/ConsistentLoader';
import PremiumActivityModalShell from '../components/PremiumActivityModalShell';
import PremiumAccordion from '../components/PremiumAccordion';
import { coLearningInkBody, coLearningInkMuted, coLearningInkTitle } from '../ui/coLearningModalTheme';

import { API_ENDPOINTS } from '../config/backendConfig';
import { artStyles } from '../data/artStyles';
import { firebaseConfig } from '../firebase';
import KidBackground from '../ui/backgrounds/KidBackground';
import { resolveImageUrl as resolveImageUrlUtil, warmResolvedImageUrls } from '../utils/urlResolver';
import { goToArtistCorner } from '../utils/navigationPaths';
import splashImage from '../assets/splash.png';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { getCache, setCache, createCacheKey, clearCache } from '../utils/cache';
import { useFeatureQuota } from '../hooks/useFeatureQuota';
import UpgradeQuotaModal from '../components/UpgradeQuotaModal';
import SmartImage from '../components/SmartImage';
import AppreciationHotspotPreview from '../components/AppreciationHotspotPreview';
import { formatActivityDate, normalizeActivityTimestamps } from '../utils/date';


const ENV = (() => {
  if (typeof window !== 'undefined') {
    const host = window.location.hostname || '';
    if (host.includes('staging')) {
      return 'staging';
    }
  }
  if (typeof process !== 'undefined' && process.env && process.env.REACT_APP_ENV) {
    return process.env.REACT_APP_ENV;
  }
  return 'prod';
})();

const activityDashboardSnapshotKey = (uid) => `activity-dashboard:activities-snapshot:${uid || 'anon'}`;

const readActivityDashboardSnapshot = (uid) => {
  const key = activityDashboardSnapshotKey(uid);
  try {
    const local = localStorage.getItem(key);
    if (local) return JSON.parse(local);
  } catch (_) {}
  try {
    const session = sessionStorage.getItem(key);
    if (session) return JSON.parse(session);
  } catch (_) {}
  return null;
};

const writeActivityDashboardSnapshot = (uid, value) => {
  const key = activityDashboardSnapshotKey(uid);
  const payload = JSON.stringify(value);
  try {
    localStorage.setItem(key, payload);
  } catch (_) {}
  try {
    sessionStorage.setItem(key, payload);
  } catch (_) {}
};

const normalizeActivityImageFields = (activity) => {
  if (!activity || typeof activity !== 'object') return activity;
  const next = normalizeActivityTimestamps({ ...activity });
  if (typeof next.imageUrl === 'string') next.imageUrl = resolveImageUrlUtil(next.imageUrl) || next.imageUrl;
  if (typeof next.artworkUrl === 'string') next.artworkUrl = resolveImageUrlUtil(next.artworkUrl) || next.artworkUrl;
  if (typeof next.originalImageUrl === 'string') next.originalImageUrl = resolveImageUrlUtil(next.originalImageUrl) || next.originalImageUrl;
  if (typeof next.transformedImageUrl === 'string') next.transformedImageUrl = resolveImageUrlUtil(next.transformedImageUrl) || next.transformedImageUrl;
  return next;
};

const normalizeActivitiesList = (list) =>
  Array.isArray(list) ? list.map((a) => normalizeActivityImageFields(a)) : [];

export default function ActivityDashboard() {
  // Detect mobile device
  const [isMobile, setIsMobile] = useState(false);
  
  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent) || 
                             window.innerWidth < 768 ||
                             ('ontouchstart' in window);
      setIsMobile(isMobileDevice);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Add CSS animation for gradient shift
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @keyframes gradientShift {
        0% { background-position: 0% 50%; }
        50% { background-position: 100% 50%; }
        100% { background-position: 0% 50%; }
      }
      /* Mobile expanded modal - force fullscreen and break out */
      .mobile-expanded-modal {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        right: 0 !important;
        bottom: 0 !important;
        width: 100vw !important;
        height: 100vh !important;
        max-width: 100vw !important;
        max-height: 100vh !important;
        margin: 0 !important;
        padding: 0 !important;
        z-index: 2147483647 !important;
        overflow: hidden !important;
        background-color: rgba(0, 0, 0, 0.95) !important;
        transform: none !important;
      }
      /* Hide ALL content when expanded on mobile - including React root */
      body.mobile-expanded-active #root > *:not(.mobile-expanded-modal),
      body.mobile-expanded-active > *:not(.mobile-expanded-modal),
      body.mobile-expanded-active .appreciation-modal-container {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
        height: 0 !important;
        overflow: hidden !important;
      }
      /* Force hide appreciation modal when expanded state is active */
      body.mobile-expanded-active .appreciation-modal-container {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        pointer-events: none !important;
        z-index: -1 !important;
      }
      
      /* Force hide artwork analysis header when expanded */
      .expanded-artwork-container .artwork-analysis-header-container,
      body.mobile-expanded-active .artwork-analysis-header-container {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        height: 0 !important;
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      
      /* Force hide amber header strip when expanded */
      .expanded-artwork-container .bg-gradient-to-r.from-amber-400.to-amber-500,
      body.mobile-expanded-active .bg-gradient-to-r.from-amber-400.to-amber-500,
      .expanded-artwork-container [class*="from-amber-400"],
      body.mobile-expanded-active [class*="from-amber-400"] {
        display: none !important;
        visibility: hidden !important;
        opacity: 0 !important;
        height: 0 !important;
        overflow: hidden !important;
        margin: 0 !important;
        padding: 0 !important;
      }
      /* Ensure modal is always on top */
      .mobile-expanded-modal,
      .mobile-expanded-modal * {
        position: relative;
        z-index: inherit;
      }
      /* Force expand button to top right in ActivityDashboard saved activity cards ONLY */
      /* Only target ActivityDashboard modal, not Magic Studio - use more specific selector */
      .appreciation-modal-container[data-activity-dashboard="true"] button[title="Expand Artwork"],
      .appreciation-modal-container[data-activity-dashboard="true"] button[title="Expand Artwork"] *,
      .appreciation-modal-container[data-activity-dashboard="true"] .bg-gradient-to-br.from-amber-50 button[title="Expand Artwork"],
      .appreciation-modal-container[data-activity-dashboard="true"] .bg-gradient-to-br.from-amber-50 button[title="Expand Artwork"] *,
      .appreciation-modal-container[data-activity-dashboard="true"] [style*="linear-gradient"] button[title="Expand Artwork"],
      .appreciation-modal-container[data-activity-dashboard="true"] [style*="linear-gradient"] button[title="Expand Artwork"] *,
      .appreciation-modal-container[data-activity-dashboard="true"] button[title="Expand artwork"],
      .appreciation-modal-container[data-activity-dashboard="true"] button[title="Expand artwork"] *,
      .appreciation-modal-container[data-activity-dashboard="true"] .expand-artwork-button,
      .appreciation-modal-container[data-activity-dashboard="true"] .expand-artwork-button * {
        top: 8px !important;
        right: 8px !important;
        bottom: auto !important;
        left: auto !important;
        position: absolute !important;
        z-index: 999 !important;
        transform: none !important;
        margin: 0 !important;
      }
      /* Hide buttons at bottom-right in ActivityDashboard ONLY */
      .appreciation-modal-container[data-activity-dashboard="true"] button[style*="bottom-4"],
      .appreciation-modal-container[data-activity-dashboard="true"] button[style*="bottom: 16px"],
      .appreciation-modal-container[data-activity-dashboard="true"] button[style*="bottom:16px"] {
        display: none !important;
      }
      /* Hide any buttons positioned at bottom */
      .appreciation-modal-container button[style*="bottom"]:not([title="Close"]):not([title="Close Expanded View"]):not([title="Expand Artwork"]):not([title="Expand artwork"]),
      .appreciation-modal-container .expand-artwork-button[style*="bottom"] {
        display: none !important;
      }
      /* Force close button to top right in expanded view */
      button[title="Close Expanded View"],
      button[title="Close Expanded View"] *,
      .fixed.inset-0 button[title="Close Expanded View"],
      .fixed.inset-0 button[title="Close Expanded View"] * {
        top: 12px !important;
        right: 12px !important;
        bottom: auto !important;
        left: auto !important;
        position: absolute !important;
        z-index: 110 !important;
      }
      /* Hide ANY buttons at bottom right */
      .appreciation-modal-container button[style*="bottom"]:not([title="Close"]):not([title="Close Expanded View"]):not([title="Expand Artwork"]):not([title="Expand artwork"]),
      .appreciation-modal-container button[style*="bottom-"]:not([title="Close"]):not([title="Close Expanded View"]):not([title="Expand Artwork"]):not([title="Expand artwork"]),
      button[style*="bottom"]:not([title="Close"]):not([title="Close Expanded View"]):not([title="Expand Artwork"]):not([title="Expand artwork"]) {
        display: none !important;
      }
      /* Ensure artwork container allows button to show */
      .appreciation-modal-container .bg-gradient-to-br.from-amber-50,
      .appreciation-modal-container [style*="linear-gradient"] {
        overflow: visible !important;
        position: relative !important;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);
  const [activities, setActivities] = useState(() => {
    try {
      const initialUid = auth?.currentUser?.uid;
      const saved = readActivityDashboardSnapshot(initialUid);
      if (Array.isArray(saved) && saved.length > 0) {
        return saved.map((a) => normalizeActivityImageFields(a));
      }
    } catch (_) {}
    return [];
  });
  const [loading, setLoading] = useState(() => {
    try {
      const initialUid = auth?.currentUser?.uid;
      const saved = readActivityDashboardSnapshot(initialUid);
      return !(Array.isArray(saved) && saved.length > 0);
    } catch (_) {
      return true;
    }
  });
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [currentView, setCurrentView] = useState('magic-studio'); // 'magic-studio', 'once-upon-click', 'daily-acts' (main 4-card page removed)
  const [highlightedActivityId, setHighlightedActivityId] = useState(null);
  const [showNotification, setShowNotification] = useState(false);
  const [notificationMessage, setNotificationMessage] = useState('');

  const [selectedActivity, setSelectedActivity] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  const [showWaysOfSeeing, setShowWaysOfSeeing] = useState(false);
  const [selectedOriginalArtwork, setSelectedOriginalArtwork] = useState(null);
  const [selectedOriginalArtworkTitle, setSelectedOriginalArtworkTitle] = useState('');
  const [visibleBoxIndex, setVisibleBoxIndex] = useState(null);
  const [showTipBox, setShowTipBox] = useState(false);
  const [showTechniques, setShowTechniques] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showTechniquePopup, setShowTechniquePopup] = useState(false);
  const [selectedTechnique, setSelectedTechnique] = useState(null);
  
  // Art Appreciation Modal state
  const [showAppreciationModal, setShowAppreciationModal] = useState(false);
  const [selectedAppreciationActivity, setSelectedAppreciationActivity] = useState(null);
  const [appreciationVisibleBoxIndex, setAppreciationVisibleBoxIndex] = useState(null);
  const [appreciationActiveSection, setAppreciationActiveSection] = useState(null);
  const [forceRender, setForceRender] = useState(0);
  const [hasTutorial, setHasTutorial] = useState(false);
  const [showTutorialModal, setShowTutorialModal] = useState(false);
  const [tutorialStyleInfo, setTutorialStyleInfo] = useState(null);
  const [tutorialImage, setTutorialImage] = useState(null);

  // Magic Studio artwork picker modal (same flow as My Art World — opens on this page)
  const [showMagicPicker, setShowMagicPicker] = useState(false);
  const [magicArtworks, setMagicArtworks] = useState([]);
  const [magicArtworksLoading, setMagicArtworksLoading] = useState(false);
  const [selectedMagicArtwork, setSelectedMagicArtwork] = useState(null);
  const [showPlayChoiceCards, setShowPlayChoiceCards] = useState(false);

  const { showUpgradeModal, dismissUpgradeModal, upgradeModalFeature } = useFeatureQuota();

  // Pre-warm visible/likely-next artwork images for smoother dashboard scrolling.
  useEffect(() => {
    const sources = [
      ...(activities || []).flatMap((activity) => {
        const journey = Array.isArray(activity?.journey) ? activity.journey : [];
        const journeyUrls = journey.map((step) => step?.transformedImageUrl).filter(Boolean);
        return [
          activity?.imageUrl,
          activity?.artworkUrl,
          activity?.originalImageUrl,
          activity?.transformedImageUrl,
          ...journeyUrls
        ].filter(Boolean);
      }),
      ...(magicArtworks || []).flatMap((artwork) => [artwork?.imageUrl, artwork?.artworkUrl].filter(Boolean))
    ];
    warmResolvedImageUrls(sources, 36).catch(() => {});
  }, [activities, magicArtworks]);

  // Force expand button position via DOM manipulation - ONLY for ActivityDashboard saved activity cards
  // NOTE: This must be AFTER all state declarations to avoid TDZ errors
  useEffect(() => {
    // Guard: Only run in browser environment
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    if (!showAppreciationModal) return;
    
    const forceButtonPosition = () => {
      try {
        // ONLY target buttons in ActivityDashboard's appreciation modal (not Magic Studio)
        // Check if we're in ActivityDashboard by looking for the data attribute
        const modalContainer = document.querySelector('.appreciation-modal-container[data-activity-dashboard="true"]');
        if (!modalContainer) return;
        
        // Find expand buttons ONLY in ActivityDashboard modal
        const buttons = modalContainer.querySelectorAll('button[title="Expand Artwork"], .expand-artwork-button, button[title="Expand artwork"]');
        buttons.forEach((button) => {
          if (button && button.getAttribute('title')?.includes('Expand')) {
            // Use setProperty with important flag
            button.style.setProperty('top', '8px', 'important');
            button.style.setProperty('right', '8px', 'important');
            button.style.setProperty('bottom', 'auto', 'important');
            button.style.setProperty('left', 'auto', 'important');
            button.style.setProperty('position', 'absolute', 'important');
            button.style.setProperty('z-index', '999', 'important');
            button.style.setProperty('transform', 'none', 'important');
            button.style.setProperty('margin', '0', 'important');
            console.log('🔧 [ACTIVITY DASHBOARD] Forced expand button to top-right:', button);
          }
        });
        
        // Hide any buttons at bottom-right in ActivityDashboard modal only
        const allButtons = modalContainer.querySelectorAll('button');
        allButtons.forEach((button) => {
          try {
            const style = window.getComputedStyle(button);
            const bottom = style.bottom;
            const title = button.getAttribute('title') || '';
            // Only hide if it's at bottom and not a close/expand button
            if (bottom !== 'auto' && bottom !== '0px' && !title.includes('Close') && !title.includes('Expand')) {
              button.style.setProperty('display', 'none', 'important');
            }
          } catch (e) {
            // Silently ignore errors for individual buttons
          }
        });
      } catch (error) {
        console.error('Error in forceButtonPosition:', error);
      }
    };
    
    // Run with delays to catch late-rendered buttons
    const timeout1 = setTimeout(forceButtonPosition, 0);
    const timeout2 = setTimeout(forceButtonPosition, 100);
    const timeout3 = setTimeout(forceButtonPosition, 300);
    const timeout4 = setTimeout(forceButtonPosition, 500);
    
    // Also run when modal content changes
    let observer = null;
    try {
      observer = new MutationObserver(() => {
        forceButtonPosition();
      });
      
      const modalContainer = document.querySelector('.appreciation-modal-container[data-activity-dashboard="true"]');
      if (modalContainer) {
        observer.observe(modalContainer, { childList: true, subtree: true, attributes: true });
      }
    } catch (error) {
      console.error('Error setting up MutationObserver:', error);
    }
    
    return () => {
      clearTimeout(timeout1);
      clearTimeout(timeout2);
      clearTimeout(timeout3);
      clearTimeout(timeout4);
      if (observer) {
        observer.disconnect();
      }
    };
  }, [showAppreciationModal, expanded]);
  const [tutorialKey, setTutorialKey] = useState(0);
  const [showTransformationNavigation, setShowTransformationNavigation] = useState(false);
  const [selectedOriginalArtworkForNavigation, setSelectedOriginalArtworkForNavigation] = useState(null);
  const [selectedOriginalArtworkTitleForNavigation, setSelectedOriginalArtworkTitleForNavigation] = useState('');
  const [initialActivityForNavigation, setInitialActivityForNavigation] = useState(null);
  const navigate = useNavigate();

  // Fetch user artworks for Magic Studio picker (same as My Art World)
  const fetchUserArtworksForMagic = async () => {
    const uid = auth.currentUser?.uid;
    if (!uid) return [];
    try {
      const [artworksSnap, sharedSnap] = await Promise.all([
        getDocs(query(collection(db, 'artworks'), where('uid', '==', uid))),
        getDocs(query(collection(db, 'sharedArtworks'), where('uid', '==', uid))),
      ]);
      const fromArtworks = artworksSnap.docs.map((d) => ({ id: d.id, ...d.data(), source: 'artworks' }));
      const fromShared = sharedSnap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          title: data.artworkTitle || data.title || '',
          imageUrl: data.artworkUrl || data.imageUrl || '',
          thumbnailUrl: data.thumbnailUrl || data.artworkThumbnailUrl || data.artworkThumbnail || data.artworkUrl || data.imageUrl || '',
          technique: data.artworkTechnique || data.technique || '',
          inspiration: data.artworkInspiration || data.inspiration || '',
          source: 'sharedArtworks',
        };
      });
      return [...fromArtworks, ...fromShared].map((a) => ({
        ...a,
        thumbnailUrl: a.thumbnailUrl || a.imageUrl || a.artworkUrl || ''
      })).filter((a) => a.imageUrl || a.thumbnailUrl);
    } catch (err) {
      console.error('[ActivityDashboard] fetchUserArtworksForMagic:', err);
      return [];
    }
  };

  useEffect(() => {
    if (!showMagicPicker) return;
    setMagicArtworksLoading(true);
    fetchUserArtworksForMagic()
      .then(setMagicArtworks)
      .finally(() => setMagicArtworksLoading(false));
  }, [showMagicPicker]);

  // Open Art Appreciation modal with the same full layout as the Art Appreciation flow
  const openAppreciationDetails = (activity) => {
    if (!activity) return;
    // Close expanded modal if open
    if (expanded) {
      setExpanded(false);
    }
    setSelectedAppreciationActivity(activity);
    setShowAppreciationModal(true);
    setAppreciationVisibleBoxIndex(null);
    setAppreciationActiveSection(null);
  };
  
  // Toggle accordion section for appreciation modal
  const toggleAppreciationSection = (sectionName) => {
    setAppreciationActiveSection(appreciationActiveSection === sectionName ? null : sectionName);
  };
  const { isAuthenticated, logout } = useAuth();

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (_) {}
    try {
      localStorage.removeItem('mentor_session');
      window.dispatchEvent(new Event('mentor_session_changed'));
    } catch (_) {}
    navigate('/');
  };
  const location = useLocation();

  // Initialize currentView from ?view= param or navigation state (main 4-card page no longer used)
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const viewQuery = (params.get('view') || location?.state?.view || '').toString();
      if (viewQuery === 'once-upon-click') {
        navigate('/my-art-stories', { replace: true });
        return;
      }
      if (viewQuery && ['magic-studio', 'daily-acts'].includes(viewQuery)) {
        setCurrentView(viewQuery);
      }
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ensure only one global details modal at a time across components
  useEffect(() => {
    const handler = () => {
      setShowDetailsModal(false);
      setSelectedActivity(null);
    };
    window.addEventListener('close-global-details-modal', handler);
    return () => window.removeEventListener('close-global-details-modal', handler);
  }, []);

  // Handle body scroll and class when expanded (mobile-specific only) - Also close appreciation modal
  useEffect(() => {
    console.log('🔍 [EXPANDED EFFECT] useEffect triggered - expanded:', expanded, 'isMobile:', isMobile);
    if (expanded) {
      console.log('🔍 [EXPANDED EFFECT] Expanded is TRUE - closing appreciation modal');
      console.log('🔍 [EXPANDED EFFECT] Current showAppreciationModal state:', showAppreciationModal);
      
      // CRITICAL: Force close appreciation modal immediately when expanded becomes true
      setShowAppreciationModal(false);
      console.log('🔍 [EXPANDED EFFECT] Set showAppreciationModal to false');
      
      // Also force hide any appreciation modal containers in the DOM
      const appreciationModals = document.querySelectorAll('.appreciation-modal-container');
      console.log('🔍 [EXPANDED EFFECT] Found appreciation modals in DOM:', appreciationModals.length);
      appreciationModals.forEach((modal, index) => {
        console.log(`🔍 [EXPANDED EFFECT] Hiding modal ${index} via DOM manipulation`);
        modal.style.display = 'none';
        modal.style.visibility = 'hidden';
        modal.style.opacity = '0';
        modal.style.pointerEvents = 'none';
        modal.style.zIndex = '-1';
      });
      
      if (isMobile) {
        console.log('🔍 [EXPANDED EFFECT] Mobile detected - preventing body scroll');
        // Prevent body scroll on mobile only
        document.body.style.overflow = 'hidden';
        document.body.style.position = 'fixed';
        document.body.style.width = '100%';
        document.body.style.height = '100%';
        // Add class to body to hide all content on mobile
        document.body.classList.add('mobile-expanded-active');
        console.log('🔍 [EXPANDED EFFECT] Added mobile-expanded-active class to body');
      }
    } else {
      console.log('🔍 [EXPANDED EFFECT] Expanded is FALSE - restoring body scroll');
      // Restore body scroll
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      // Remove class
      document.body.classList.remove('mobile-expanded-active');
      console.log('🔍 [EXPANDED EFFECT] Removed mobile-expanded-active class from body');
    }
    return () => {
      document.body.style.overflow = '';
      document.body.style.position = '';
      document.body.style.width = '';
      document.body.style.height = '';
      document.body.classList.remove('mobile-expanded-active');
    };
  }, [expanded, isMobile]);

  // Debug useEffect to monitor modal state
  useEffect(() => {
    console.log('🎨 Modal state changed - showDetailsModal:', showDetailsModal, 'selectedActivity:', !!selectedActivity);
    if (showDetailsModal && selectedActivity) {
      console.log('🎨 Modal should be visible now!');
      // Force a re-render after a short delay
      setTimeout(() => {
        console.log('🎨 Checking if modal is still visible...');
        console.log('🎨 Current state - showDetailsModal:', showDetailsModal, 'selectedActivity:', !!selectedActivity);
      }, 100);
    }
  }, [showDetailsModal, selectedActivity]);

  // Lock body scroll while any overlay is open
  useEffect(() => {
    const previous = document.body.style.overflow;
    
    // Check if any overlay is open
    const isOverlayOpen = showDetailsModal || expanded || showTutorialModal || showTransformationNavigation || showWaysOfSeeing;
    
    console.log('🎨 ActivityDashboard overlay states:', {
      showDetailsModal,
      expanded,
      showTutorialModal,
      showTransformationNavigation,
      showWaysOfSeeing,
      isOverlayOpen
    });
    
    if (isOverlayOpen) {
      document.body.style.overflow = 'hidden';
      console.log('🎨 Setting black background for overlay');
    } else {
      document.body.style.overflow = previous || '';
      console.log('🎨 Restoring scroll');
      // Ensure selectedActivity is cleared when no overlays are open
      if (selectedActivity) {
        console.log('🧹 Clearing selectedActivity as no overlays are open');
        setSelectedActivity(null);
      }
    }
    
    return () => {
      document.body.style.overflow = previous || '';
    };
  }, [showDetailsModal, expanded, showTutorialModal, showTransformationNavigation, showWaysOfSeeing, selectedActivity]);

  // Force close appreciation modal when expanded is true - Critical for mobile/PWA
  useEffect(() => {
    console.log('🔍 [APPRECIATION MODAL EFFECT] useEffect triggered - expanded:', expanded, 'showAppreciationModal:', showAppreciationModal);
    if (expanded) {
      console.log('🔒 [APPRECIATION MODAL EFFECT] Expanded is true, closing appreciation modal');
      setShowAppreciationModal(false);
      console.log('🔒 [APPRECIATION MODAL EFFECT] Set showAppreciationModal to false');
    }
  }, [expanded]);

  // Group activities by original artwork
  const groupActivitiesByArtwork = (view = 'main') => {
    const grouped = {};
    
    console.log('🎯 [DASHBOARD] groupActivitiesByArtwork called with view:', view);
    console.log('🎯 [DASHBOARD] Activities to group:', activities.length);
    console.log('🎨 Grouping activities:', activities);
    
    // Debug: Check for journey activities specifically
    const journeyActivities = activities.filter(a => a.type === 'reimagine' && a.metadata?.feature === 'ways-of-seeing-journey');
    console.log('🎨 Journey activities found:', journeyActivities.length);
    journeyActivities.forEach((ja, index) => {
      console.log(`🎨 Journey ${index + 1}:`, {
        id: ja.id,
        hasJourney: !!ja.journey,
        journeyLength: ja.journey?.length || 0,
        originalImageUrl: ja.originalImageUrl,
        metadata: ja.metadata
      });
    });
    
    activities.forEach(activity => {
      // Skip tutorial activities - they should not appear as separate cards
      if (activity.type === 'tutorial') {
        console.log(`🎨 Skipping tutorial activity ${activity.id} - tutorials should be embedded in reimagine activities`);
        return;
      }

      // Skip old art-history-appreciation and art-comparison; we now save a single flow per artwork (artHistoryFlows)
      const isOldArtHistory = activity.type === 'art-history-appreciation' || activity.metadata?.feature === 'art-history-appreciation' || activity.feature === 'art-history-appreciation';
      const isOldArtComparison = activity.type === 'art-comparison' || activity.metadata?.feature === 'art-comparison' || activity.feature === 'art-comparison';
      if (isOldArtHistory || isOldArtComparison) {
        console.log(`🎨 Skipping old art history activity ${activity.id} - use Learn Art History flow instead`);
        return;
      }
      
      // Skip daily activities in Magic Studio view - they should only appear in Daily Acts view
      if (activity.type === 'daily_activity' && view === 'magic-studio') {
        console.log(`🎨 Skipping daily activity ${activity.id} - daily activities should not appear in Magic Studio view`);
        return;
      }
      
      // For daily-acts view, only show daily activities
      if (view === 'daily-acts' && activity.type !== 'daily_activity') {
        console.log(`🎨 Skipping non-daily activity ${activity.id} - only daily activities should appear in Daily Acts view`);
        return;
      }
      
      // Debug: Log reimagine activities to see their metadata
      if (activity.type === 'reimagine') {
        console.log(`🎨 Reimagine activity ${activity.id}:`, {
          feature: activity.metadata?.feature,
          prompt: activity.metadata?.prompt,
          userFeedback: activity.metadata?.userFeedback,
          artmosphereFeedback: activity.metadata?.artmosphereFeedback,
          hasJourney: !!activity.journey,
          journeySteps: activity.journey?.length || 0
        });
      }
      
      let artworkKey = '';
      
      // Helper function to extract URL from potentially object values
      const extractUrl = (urlValue) => {
        if (typeof urlValue === 'string') {
          return urlValue;
        } else if (urlValue && typeof urlValue === 'object') {
          // Handle case where originalImageUrl was saved as an object
          const extracted = urlValue.imageUrl || urlValue.url || urlValue.originalArtwork || urlValue;
          // If the extracted value is still an object, try to stringify it for consistency
          if (typeof extracted === 'object') {
            return JSON.stringify(extracted);
          }
          return extracted;
        }
        return null;
      };
      
      // Try original logic first, then fallback to extractUrl if needed
      let originalArtworkKey = '';
      
      // Check for art history activities first (by type, feature, or title pattern)
      const isArtHistoryAppreciation = activity.type === 'art-history-appreciation' || 
                                       activity.metadata?.feature === 'art-history-appreciation' ||
                                       activity.feature === 'art-history-appreciation' ||
                                       (activity.title && activity.title.includes('Art History:'));
      
      const isArtComparison = activity.type === 'art-comparison' || 
                             activity.metadata?.feature === 'art-comparison' ||
                             activity.feature === 'art-comparison' ||
                             (activity.title && activity.title.includes('Comparing:'));
      
      if (activity.type === 'appreciation' && !isArtHistoryAppreciation) {
        originalArtworkKey = activity.originalImageUrl || activity.imageUrl || 'unknown';
      } else if (isArtHistoryAppreciation) {
        originalArtworkKey = 'art-history-appreciation-all';
      } else if (isArtComparison) {
        originalArtworkKey = 'art-comparison-all';
      } else if (activity.type === 'reimagine') {
        originalArtworkKey = activity.originalImageUrl || activity.metadata?.originalArtworkId || activity.imageUrl || 'unknown';
      } else if (activity.type === 'story') {
        originalArtworkKey = activity.originalImageUrl || activity.metadata?.originalArtworkId || activity.imageUrl || 'unknown';
      } else if (activity.type === 'daily_activity') {
        // For daily activities, use a unique key based on activity ID
        originalArtworkKey = `daily_activity_${activity.activityId || activity.id}`;
      }
      
      // Use extractUrl only if the original key is an object
      if (typeof originalArtworkKey === 'object') {
        artworkKey = extractUrl(originalArtworkKey) || 'unknown';
      } else {
        artworkKey = originalArtworkKey;
      }
      
      // Debug: Log the artwork key resolution
      console.log(`🎨 Activity ${activity.id} artwork key resolution:`, {
        type: activity.type,
        originalImageUrl: activity.originalImageUrl,
        originalImageUrlType: typeof activity.originalImageUrl,
        imageUrl: activity.imageUrl,
        metadataOriginalArtworkId: activity.metadata?.originalArtworkId,
        extractedUrl: extractUrl(activity.originalImageUrl),
        finalArtworkKey: artworkKey
      });
      
      console.log(`🎨 Activity ${activity.id} (${activity.type}) -> artworkKey: ${artworkKey}`);
      
      // Skip activities without a valid artwork URL
      if (artworkKey === 'unknown' || !artworkKey) {
        console.log(`🎨 Skipping activity ${activity.id} - no valid artwork URL found`);
        return;
      }
      
      // Handle journey-based activities (ways-of-seeing-journey)
      if (activity.type === 'reimagine' && activity.metadata?.feature === 'ways-of-seeing-journey' && activity.journey && activity.journey.length > 0) {
        console.log(`🎨 Processing journey activity ${activity.id} with ${activity.journey.length} steps`);
        console.log(`🎨 Journey data:`, {
          journey: activity.journey,
          originalImageUrl: activity.originalImageUrl,
          artworkKey: artworkKey
        });
        
        // Create individual activities for each journey step
        activity.journey.forEach((step, stepIndex) => {
          const stepActivity = {
            id: `${activity.id}_step_${stepIndex + 1}`,
            type: 'reimagine',
            title: `${step.styleInfo?.name || 'Style'} Transformation - Step ${step.step || stepIndex + 1}`,
            description: step.prompt || `Transformation step ${step.step || stepIndex + 1}`,
            originalImageUrl: activity.originalImageUrl,
            transformedImageUrl: step.transformedImageUrl,
            styleInfo: step.styleInfo,
            metadata: {
              ...activity.metadata,
              isJourneyStep: true,
              stepNumber: step.step || stepIndex + 1,
              parentJourneyId: activity.id
            },
            createdAt: step.timestamp || activity.createdAt,
            updatedAt: step.timestamp || activity.updatedAt
          };
          
          // Add the step activity to the grouped activities
          if (!grouped[artworkKey]) {
            grouped[artworkKey] = {
              artworkUrl: artworkKey,
              activities: [],
              title: activity.title || 'Untitled Artwork',
              originalTitle: activity.title || 'Untitled Artwork'
            };
          }
          
          grouped[artworkKey].activities.push(stepActivity);
          console.log(`🎨 Added journey step activity: ${stepActivity.id}`, {
            title: stepActivity.title,
            artworkKey: artworkKey,
            totalActivitiesInGroup: grouped[artworkKey].activities.length
          });
        });
        
        // Don't add the main journey document as a separate activity
        return;
      } else if (activity.type === 'reimagine' && activity.metadata?.feature === 'ways-of-seeing-journey' && (!activity.journey || activity.journey.length === 0)) {
        // Handle initial journey creation (before steps are added)
        console.log(`🎨 Processing initial journey activity ${activity.id} - no steps yet`);
        
        // Create a single activity for the initial transformation
        const initialActivity = {
          id: `${activity.id}_initial`,
          type: 'reimagine',
          title: `${activity.styleInfo?.name || 'Style'} Transformation - Initial`,
          description: 'Initial artwork transformation',
          originalImageUrl: activity.originalImageUrl,
          transformedImageUrl: activity.transformedImageUrl,
          styleInfo: activity.styleInfo,
          metadata: {
            ...activity.metadata,
            isJourneyStep: true,
            stepNumber: 1,
            parentJourneyId: activity.id
          },
          createdAt: activity.createdAt,
          updatedAt: activity.updatedAt
        };
        
        // Add the initial activity to the grouped activities
        if (!grouped[artworkKey]) {
          grouped[artworkKey] = {
            artworkUrl: artworkKey,
            activities: [],
            title: activity.title || 'Untitled Artwork',
            originalTitle: activity.title || 'Untitled Artwork'
          };
        }
        
        grouped[artworkKey].activities.push(initialActivity);
        console.log(`🎨 Added initial journey activity: ${initialActivity.id}`);
        
        // Don't add the main journey document as a separate activity
        return;
      }
      
      // For debugging: log all keys being created
      console.log(`🎨 Creating/using key: "${artworkKey}" for activity ${activity.id}`);
      if (grouped[artworkKey]) {
        console.log(`🎨 Key "${artworkKey}" already exists with ${grouped[artworkKey].activities.length} activities`);
      } else {
        console.log(`🎨 Creating new group for key "${artworkKey}"`);
      }
      
      if (!grouped[artworkKey]) {
        // Get the original artwork title - look in multiple possible locations
        let originalTitle = 'Untitled Artwork';
        
        // Try to find the original artwork name from various possible fields
        if (activity.originalArtworkTitle) {
          originalTitle = activity.originalArtworkTitle;
        } else if (activity.metadata?.originalArtworkTitle) {
          originalTitle = activity.metadata.originalArtworkTitle;
        } else if (activity.metadata?.originalArtworkName) {
          originalTitle = activity.metadata.originalArtworkName;
        } else if (activity.originalArtworkName) {
          originalTitle = activity.originalArtworkName;
        } else if (activity.artworkName) {
          originalTitle = activity.artworkName;
        } else if (activity.type === 'appreciation' && activity.title) {
          // For appreciation activities, the title is usually the original artwork
          originalTitle = activity.title;
        } else if (activity.type === 'reimagine' && activity.metadata?.originalTitle) {
          originalTitle = activity.metadata.originalTitle;
        } else if (activity.originalTitle) {
          originalTitle = activity.originalTitle;
        }
        
        // If we still have 'Untitled Artwork', try to get it from the first activity's title
        if (originalTitle === 'Untitled Artwork' && activity.title && !activity.title.includes('Reimagine') && !activity.title.includes('Appreciation')) {
          originalTitle = activity.title;
        }
        
        grouped[artworkKey] = {
          artworkUrl: artworkKey,
          activities: [],
          title: originalTitle,
          originalTitle: originalTitle
        };
      }
      
      grouped[artworkKey].activities.push(activity);
    });
    
    console.log('🎨 Grouped result:', grouped);
    
    // Debug: Show what's in each group
    Object.keys(grouped).forEach(key => {
      console.log(`🎨 Group "${key}":`, {
        title: grouped[key].title,
        activitiesCount: grouped[key].activities.length,
        activities: grouped[key].activities.map(a => ({
          id: a.id,
          title: a.title,
          type: a.type,
          isJourneyStep: a.metadata?.isJourneyStep
        }))
      });
    });
    
    console.log('🎨 Final groups created:');
    Object.keys(grouped).forEach(key => {
      console.log(`  - Key: "${key}" -> ${grouped[key].activities.length} activities`);
    });
    return Object.values(grouped);
  };

  // Get grouped artworks based on current view
  const getGroupedArtworksForView = () => {
    if (currentView === 'magic-studio') {
      // For Magic Studio, include all activity types except tutorials
      return groupActivitiesByArtwork('magic-studio');
    } else if (currentView === 'daily-acts') {
      // For Daily Acts, only include daily activities
      return groupActivitiesByArtwork('daily-acts');
    } else {
      // For main view, include all activities
      return groupActivitiesByArtwork('main');
    }
  };

  const [groupedArtworks, setGroupedArtworks] = useState([]);

  // Update grouped artworks when currentView changes
  useEffect(() => {
    const artworks = getGroupedArtworksForView();
    setGroupedArtworks(artworks);
  }, [currentView, activities]);

  useEffect(() => {
    // Wait for authentication to be ready
    let realtimeUnsubscribe = null;
    let loadingTimeout = null;
    
    // Safety timeout: if loading takes more than 10 seconds, force stop loading
    loadingTimeout = setTimeout(() => {
      console.warn('⚠️ [LOADING TIMEOUT] Loading took more than 10 seconds, forcing loading to false');
      setLoading(false);
    }, 10000);
    
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        console.log('🔐 User authenticated, fetching activities...');

        // Instant hydrate from persisted snapshot on login/refresh.
        try {
          const saved = readActivityDashboardSnapshot(user.uid);
          if (Array.isArray(saved) && saved.length > 0) {
            setActivities(saved.map((a) => normalizeActivityImageFields(a)));
            setLoading(false);
          }
        } catch (_) {}

        fetchActivities(user).finally(() => {
          // Clear timeout when fetchActivities completes
          if (loadingTimeout) {
            clearTimeout(loadingTimeout);
            loadingTimeout = null;
          }
        });
        
        // Set up real-time listener for activities
        try {
          realtimeUnsubscribe = setupRealtimeListener(user);
        } catch (realtimeError) {
          console.error('❌ Error setting up real-time listener:', realtimeError);
          // Don't block loading if real-time listener fails
        }
      } else {
        console.log('🔐 No user authenticated');
        setLoading(false);
        if (loadingTimeout) {
          clearTimeout(loadingTimeout);
          loadingTimeout = null;
        }
      }
    });

    return () => {
      unsubscribe();
      if (realtimeUnsubscribe) {
        realtimeUnsubscribe();
      }
      if (loadingTimeout) {
        clearTimeout(loadingTimeout);
      }
    };
  }, []);

  // Persist snapshot for instant cold-start after refresh/login.
  useEffect(() => {
    try {
      const uid = auth?.currentUser?.uid;
      if (!uid) return;
      if (!Array.isArray(activities) || activities.length === 0) return;
      writeActivityDashboardSnapshot(uid, activities.slice(0, 220));
    } catch (_) {}
  }, [activities]);

  // Handle navigation state for highlighting specific activities
  useEffect(() => {
    if (location.state) {
      const { highlightActivityId, filterType } = location.state;
      
      if (highlightActivityId) {
        setHighlightedActivityId(highlightActivityId);
        setShowNotification(true);
        setNotificationMessage('Found your existing activity! Here it is highlighted below.');
        
        // Scroll to the highlighted activity after a short delay
        setTimeout(() => {
          const element = document.getElementById(`activity-${highlightActivityId}`);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Remove highlight after 3 seconds
            setTimeout(() => {
              setHighlightedActivityId(null);
              setShowNotification(false);
            }, 3000);
          }
        }, 500);
      }
    }
  }, [location.state]);

  const fetchActivities = async (user = null) => {
    const currentUser = user || auth.currentUser;
    if (!currentUser) {
      console.log('❌ No user available for fetchActivities');
      setLoading(false);
      return;
    }

    // For user-specific data: Use real-time listener directly (immediate updates, low cost)
    // Cache is updated by the listener, so we don't need to check cache here
    // The listener will provide immediate updates when user creates/deletes activities

    try {
      setLoading(true);
      console.log('🎯 [DASHBOARD] fetchActivities called for user:', currentUser.uid);
      console.log('🎯 [DASHBOARD] Current activities state before fetch:', activities.length);
      console.log('🔍 [DEBUG] Creating query for user:', currentUser.uid);
      console.log('🔍 [DEBUG] User object:', {
        uid: currentUser.uid,
        email: currentUser.email,
        displayName: currentUser.displayName,
        isAnonymous: currentUser.isAnonymous
      });
      
      // Test basic Firebase connectivity
      console.log('🔍 [DEBUG] Testing Firebase connectivity...');
      console.log('🔍 [DEBUG] Firestore instance:', db);
      console.log('🔍 [DEBUG] Firebase config:', firebaseConfig);
      
      // First try to get activities without ordering to see if the basic query works
      console.log('🔍 [DEBUG] Trying basic query first...');
      
      // Check if there are any documents in the collection at all
      try {
        const allDocsQuery = query(collection(db, 'userActivities'));
        const allDocsSnapshot = await getDocs(allDocsQuery);
        console.log('🔍 [DEBUG] Total documents in collection:', allDocsSnapshot.docs.length);
        
        if (allDocsSnapshot.docs.length > 0) {
          console.log('🔍 [DEBUG] Sample document structure:', allDocsSnapshot.docs[0].data());
        }
      } catch (allDocsError) {
        console.log('⚠️ Could not query all documents:', allDocsError);
        console.log('⚠️ All docs error details:', {
          code: allDocsError.code,
          message: allDocsError.message
        });
        
        // Check if this is a database not found error
        if (allDocsError.code === 'failed-precondition' || allDocsError.code === 'unimplemented') {
          console.error('❌ DATABASE NOT FOUND! You need to create a Firestore database in the Firebase Console.');
          console.error('❌ Go to: https://console.firebase.google.com/project/artmosphere-89b64/firestore');
          console.error('❌ Click "Create Database" and choose a location.');
        } else if (allDocsError.code === 'permission-denied') {
          console.error('❌ PERMISSION DENIED! Check Firestore security rules.');
          console.error('❌ Current rules allow everything, so this might be a database location issue.');
        }
      }
      
      // Test if we can access a different collection (this will help determine if it's a general permission issue)
      try {
        console.log('🔍 [DEBUG] Testing access to a different collection...');
        const testCollectionQuery = query(collection(db, 'test'));
        const testSnapshot = await getDocs(testCollectionQuery);
        console.log('✅ Can access test collection, documents:', testSnapshot.docs.length);
      } catch (testError) {
        console.log('⚠️ Cannot access test collection:', testError.code, testError.message);
        
        // Check if this is a database not found error
        if (testError.code === 'failed-precondition' || testError.code === 'unimplemented') {
          console.error('❌ DATABASE NOT FOUND! You need to create a Firestore database in the Firebase Console.');
          console.error('❌ Go to: https://console.firebase.google.com/project/artmosphere-89b64/firestore');
          console.error('❌ Click "Create Database" and choose a location.');
        }
      }
      
      const basicQuery = query(
        collection(db, 'userActivities'),
        where('userId', '==', currentUser.uid)
      );
      
      const basicSnapshot = await getDocs(basicQuery);
      console.log('🔍 [DEBUG] Basic query result:', basicSnapshot.docs.length, 'documents');
      
      // Test if we can write to the database (this will help diagnose permission issues)
      if (basicSnapshot.docs.length === 0) {
        console.log('🔍 [DEBUG] No activities found, testing write permissions...');
        try {
          const testDoc = {
            userId: currentUser.uid,
            type: 'test',
            createdAt: new Date(),
            test: true
          };
          const testRef = await addDoc(collection(db, 'userActivities'), testDoc);
          console.log('✅ Write test successful, created test document:', testRef.id);
          
          // Clean up the test document
          await deleteDoc(testRef);
          console.log('✅ Test document cleaned up');
        } catch (writeError) {
          console.error('❌ Write test failed:', writeError);
          console.error('❌ Write error details:', {
            code: writeError.code,
            message: writeError.message,
            stack: writeError.stack
          });
        }
      }
      
      // Now try with ordering
      const activitiesQuery = query(
        collection(db, 'userActivities'),
        where('userId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );
      
      console.log('🔍 [DEBUG] Query created, executing...');
      
      let snapshot;
      try {
        snapshot = await getDocs(activitiesQuery);
        console.log('🔍 [DEBUG] Ordered query successful');
      } catch (orderError) {
        console.log('⚠️ Ordered query failed, using basic query result:', orderError);
        snapshot = basicSnapshot;
      }
      
      const activitiesRaw = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const activitiesData =
        ENV === 'staging'
          ? activitiesRaw.filter(a => a.environment === 'staging')
          : activitiesRaw;
      
      console.log('📊 Fetched activities:', activitiesData);
      console.log('📊 Activity types found:', activitiesData.map(a => ({ id: a.id, type: a.type, title: a.title })));
      
      // Debug: Log all reimagine activities
      const reimagineActivities = activitiesData.filter(a => a.type === 'reimagine');
      console.log('🎨 All reimagine activities found:', reimagineActivities.map(a => ({
        id: a.id,
        feature: a.metadata?.feature,
        featureType: typeof a.metadata?.feature,
        hasFeature: !!a.metadata?.feature,
        hasPrompt: !!a.metadata?.prompt,
        hasFeedback: !!a.metadata?.userFeedback,
        hasJourney: !!a.journey,
        journeyLength: a.journey?.length || 0,
        title: a.title,
        originalImageUrl: a.originalImageUrl,
        transformedImageUrl: a.transformedImageUrl,
        metadata: a.metadata
      })));
      
      // Debug: Check for activities that should show in ReimaginedArtworkCard
      const shouldShowInReimagined = reimagineActivities.filter(a => {
        const feature = a.metadata?.feature;
        return (
          (feature === 'reimagine-artwork' || feature === 'reimagine' || !feature || feature === undefined || feature === null) &&
          feature !== 'ways-of-seeing-journey' &&
          feature !== 'reimagine-with-feedback'
        );
      });
      console.log('🎨 Activities that SHOULD show in ReimaginedArtworkCard:', shouldShowInReimagined.length, shouldShowInReimagined.map(a => ({
        id: a.id,
        title: a.title,
        feature: a.metadata?.feature || 'none'
      })));
      
      // Debug: Check for ways-of-seeing-journey specifically
      const waysOfSeeingActivities = activitiesData.filter(a => a.type === 'reimagine' && a.metadata?.feature === 'ways-of-seeing-journey');
      console.log('🎨 Ways of Seeing activities found:', waysOfSeeingActivities.length);
      waysOfSeeingActivities.forEach((woa, index) => {
        console.log(`🎨 Ways of Seeing ${index + 1}:`, {
          id: woa.id,
          title: woa.title,
          hasJourney: !!woa.journey,
          journeyLength: woa.journey?.length || 0,
          originalImageUrl: woa.originalImageUrl,
          transformedImageUrl: woa.transformedImageUrl,
          metadata: woa.metadata
        });
      });
      
      // Debug: Check ALL activities to see what's actually there
      console.log('🎨 ALL activities breakdown:', activitiesData.map(a => ({
        id: a.id,
        type: a.type,
        title: a.title,
        feature: a.metadata?.feature,
        hasJourney: !!a.journey,
        journeyLength: a.journey?.length || 0
      })));
      
      console.log('🎯 [DASHBOARD] Setting activities state with:', activitiesData.length, 'activities');
      
      // Cache the results
      const cacheKey = createCacheKey('userActivities', { userId: currentUser.uid }, currentUser.uid);
      setCache(cacheKey, activitiesData, 5 * 60 * 1000); // 5 minute cache
      console.log('✅ [DASHBOARD] Activities cached');
      
      setActivities(normalizeActivitiesList(activitiesData));
    } catch (error) {
      console.error('❌ Error fetching activities:', error);
      console.error('❌ Error details:', {
        code: error.code,
        message: error.message,
        stack: error.stack
      });
      
      // Check if it's a permissions error
      if (error.code === 'permission-denied') {
        console.error('❌ This is a permissions error. Check Firestore rules and user authentication.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Set up real-time listener for activities
  const setupRealtimeListener = (user) => {
    if (!user) return;
    
    console.log('🎯 [DASHBOARD] Setting up real-time listener for user:', user.uid);
    console.log('🔍 Setting up real-time listener for activities...');
    
    // Try ordered query first, fallback to basic query if it fails
    let activitiesQuery;
    try {
      activitiesQuery = query(
        collection(db, 'userActivities'),
        where('userId', '==', user.uid),
        orderBy('createdAt', 'desc')
      );
    } catch (queryError) {
      console.log('⚠️ Ordered query failed, using basic query for real-time listener:', queryError);
      activitiesQuery = query(
        collection(db, 'userActivities'),
        where('userId', '==', user.uid)
      );
    }
    
    const unsubscribe = onSnapshot(activitiesQuery, (snapshot) => {
      console.log('🎯 [DASHBOARD] Real-time listener triggered with:', snapshot.docs.length, 'activities');
      console.log('🔄 Real-time update received:', snapshot.docs.length, 'activities');
      
      const activitiesRaw = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      const activitiesData =
        ENV === 'staging'
          ? activitiesRaw.filter(a => a.environment === 'staging')
          : activitiesRaw;
      
      // Update cache when real-time updates occur
      const cacheKey = createCacheKey('userActivities', { userId: user.uid }, user.uid);
      setCache(cacheKey, activitiesData, 5 * 60 * 1000);
      
      // Check if new activities were added
      const previousCount = activities.length;
      const newCount = activitiesData.length;
      
      console.log('🎯 [DASHBOARD] Activity count comparison:', {
        previousCount,
        newCount,
        difference: newCount - previousCount,
        hasNewActivities: newCount > previousCount
      });
      
      if (newCount > previousCount && previousCount > 0) {
        const newActivitiesCount = newCount - previousCount;
        console.log('🎯 [DASHBOARD] New activities detected! Count:', newActivitiesCount);
        setShowNotification(true);
        setNotificationMessage(`🎉 ${newActivitiesCount} new activity${newActivitiesCount > 1 ? 'ies' : 'y'} detected!`);
        
        // Auto-hide notification after 5 seconds
        setTimeout(() => {
          setShowNotification(false);
        }, 5000);
      } else {
        console.log('🎯 [DASHBOARD] No new activities detected or first load');
      }
      
      console.log('📊 Real-time activities update:', activitiesData);
      
      // Debug: Check for ways-of-seeing-journey in real-time updates
      const waysOfSeeingActivities = activitiesData.filter(a => a.type === 'reimagine' && a.metadata?.feature === 'ways-of-seeing-journey');
      console.log('🎨 Real-time Ways of Seeing activities found:', waysOfSeeingActivities.length);
      waysOfSeeingActivities.forEach((woa, index) => {
        console.log(`🎨 Real-time Ways of Seeing ${index + 1}:`, {
          id: woa.id,
          title: woa.title,
          hasJourney: !!woa.journey,
          journeyLength: woa.journey?.length || 0,
          originalImageUrl: woa.originalImageUrl,
          transformedImageUrl: woa.transformedImageUrl
        });
      });
      
      console.log('🎯 [DASHBOARD] Real-time listener setting activities with:', activitiesData.length, 'activities');
      setActivities(normalizeActivitiesList(activitiesData));
    }, (error) => {
      // Handle Firestore Listen channel errors gracefully
      if (error.code === 'permission-denied') {
        console.warn('⚠️ [DASHBOARD] Permission denied for real-time listener, falling back to getDocs');
        // Fallback to getDocs if permission denied
        getDocs(activitiesQuery).then((snapshot) => {
          const activitiesData = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setActivities(normalizeActivitiesList(activitiesData));
          setLoading(false);
        }).catch((fallbackError) => {
          console.error('❌ [DASHBOARD] Fallback getDocs also failed:', fallbackError);
          setLoading(false);
        });
      } else if (error.code === 'unavailable' || error.message?.includes('access control') || error.message?.includes('Load failed')) {
        // Network/CORS errors - these are often non-critical and will retry automatically
        console.warn('⚠️ [DASHBOARD] Network/CORS error in listener (will retry automatically):', error.code, error.message);
        // Don't set loading to false - allow automatic retry
      } else {
        // Other errors - log but don't crash
        console.warn('⚠️ [DASHBOARD] Real-time listener error (non-critical):', {
          code: error.code,
          message: error.message,
          name: error.name
        });
        setLoading(false);
      }
      
      // If there's a permission error, log it
      if (error.code === 'permission-denied') {
        console.warn('⚠️ Permission denied in real-time listener');
      }
    }, { purpose: 'ActivityDashboard-activities', component: 'ActivityDashboard' });
    
    // Store the unsubscribe function for cleanup
    return unsubscribe;
  };





  // Refresh activities - can be called after new activities are saved
  const refreshActivities = async () => {
    console.log('🔄 Manually refreshing activities...');
    const currentUser = auth.currentUser;
    if (currentUser) {
      console.log('🔄 Current user:', currentUser.uid);
      console.log('🔄 Current activities count before refresh:', activities.length);
      
      // Since the real-time listener is working, just log the current state
      console.log('🔄 Real-time listener is active, activities should update automatically');
      console.log('🔄 Current activities count:', activities.length);
    } else {
      console.log('❌ No user available for refreshActivities');
    }
  };

  // Helper function for styled alert notifications
  const showStyledAlert = (message, type = 'info') => {
    const container = document.createElement('div');
    container.className = 'fixed inset-0 z-[999999] flex items-center justify-center';
    container.style.cssText = 'padding-top: env(safe-area-inset-top, 0px); padding-left: env(safe-area-inset-left, 0px); padding-right: env(safe-area-inset-right, 0px); padding-bottom: env(safe-area-inset-bottom, 0px);';
    
    const bgColors = {
      error: 'from-red-100 to-orange-100',
      warning: 'from-amber-100 to-yellow-100',
      info: 'from-blue-100 to-cyan-100',
      success: 'from-green-100 to-emerald-100'
    };
    
    const borderColors = {
      error: 'border-red-200',
      warning: 'border-amber-200',
      info: 'border-blue-200',
      success: 'border-green-200'
    };
    
    const textColors = {
      error: 'text-red-800',
      warning: 'text-amber-800',
      info: 'text-blue-800',
      success: 'text-green-800'
    };
    
    const icons = {
      error: '⚠️',
      warning: '⚠️',
      info: 'ℹ️',
      success: '✅'
    };
    
    container.innerHTML = `
      <div class="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"></div>
      <div class="relative bg-gradient-to-br from-white via-amber-50/30 to-white rounded-2xl md:rounded-3xl p-6 md:p-8 w-full max-w-md mx-4 shadow-2xl border-2 ${borderColors[type]} backdrop-blur-sm" style="font-family: 'Baloo 2', 'Comic Sans MS', cursive;">
        <div class="flex items-center justify-center mb-4">
          <div class="w-16 h-16 rounded-full bg-gradient-to-br ${bgColors[type]} flex items-center justify-center border-2 ${borderColors[type]}">
            <span class="text-3xl">${icons[type]}</span>
          </div>
        </div>
        <p class="${textColors[type]} mb-6 text-center text-base md:text-lg font-semibold">${message}</p>
        <div class="flex justify-center">
          <button id="am-close" class="px-6 py-3 rounded-full art-btn-primary transition-all duration-200 font-bold text-sm md:text-base shadow-lg hover:shadow-xl transform hover:scale-105" style="font-family: 'Baloo 2', 'Comic Sans MS', cursive;">OK</button>
        </div>
      </div>`;
    document.body.appendChild(container);
    const cleanup = () => {
      if (document.body.contains(container)) {
        document.body.removeChild(container);
      }
    };
    container.querySelector('#am-close')?.addEventListener('click', cleanup);
    container.querySelector('.absolute.inset-0')?.addEventListener('click', cleanup);
    // Auto-close after 5 seconds
    setTimeout(cleanup, 5000);
  };

  // Track ongoing delete operations to prevent duplicate modals
  const [deletingActivityId, setDeletingActivityId] = useState(null);
  
  const handleDeleteActivity = async (activityId, retryCount = 0, skipConfirmation = false) => {
    // Prevent duplicate calls - if already deleting this activity, ignore
    if (!skipConfirmation && deletingActivityId === activityId) {
      console.log('⚠️ Delete already in progress for activity:', activityId);
      return;
    }
    
    // Artmosphere-styled confirm dialog - Enhanced aesthetic (skip if bulk delete)
    if (!skipConfirmation) {
      // Mark as deleting to prevent duplicate modals
      setDeletingActivityId(activityId);
      const confirmed = await new Promise((resolve) => {
      const container = document.createElement('div');
      container.className = 'fixed inset-0 z-[999999] flex items-center justify-center';
      container.style.cssText = 'padding-top: env(safe-area-inset-top, 0px); padding-left: env(safe-area-inset-left, 0px); padding-right: env(safe-area-inset-right, 0px); padding-bottom: env(safe-area-inset-bottom, 0px);';
      container.innerHTML = `
        <div class="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"></div>
        <div class="relative bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl border-2 border-amber-200" style="font-family: 'Baloo 2', 'Comic Sans MS', cursive;">
          <h3 class="text-2xl font-bold text-amber-800 mb-2" style="font-family: 'Baloo 2', 'Comic Sans MS', cursive;">Delete this creation?</h3>
          <p class="text-amber-700 mb-4" style="font-family: 'Baloo 2', 'Comic Sans MS', cursive;">This action cannot be undone.</p>
          <div class="flex gap-3 justify-end">
            <button id="am-cancel" class="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-200 font-semibold" style="font-family: 'Baloo 2', 'Comic Sans MS', cursive;">Cancel</button>
            <button 
              id="am-ok" 
              class="px-4 py-2 rounded-lg art-btn-primary transition-all duration-200 font-semibold" 
              style="font-family: 'Baloo 2', 'Comic Sans MS', cursive;"
            >
              Delete
            </button>
          </div>
        </div>`;
      document.body.appendChild(container);
      const cleanup = () => {
        if (document.body.contains(container)) {
          document.body.removeChild(container);
        }
      };
      const handleCancel = () => { cleanup(); resolve(false); };
      const handleOk = () => { cleanup(); resolve(true); };
      container.querySelector('#am-cancel')?.addEventListener('click', handleCancel);
      container.querySelector('#am-ok')?.addEventListener('click', handleOk);
      // Close on backdrop click
      container.querySelector('.absolute.inset-0')?.addEventListener('click', handleCancel);
      });
      if (!confirmed) {
        setDeletingActivityId(null); // Clear deleting state if cancelled
        return;
      }
      // Clear deleting state after confirmation
      setDeletingActivityId(null);
    }
    
    try {
      console.log('🗑️ Attempting to delete activity:', activityId, retryCount > 0 ? `(retry ${retryCount})` : '');
      
      // Check if user is authenticated
      const currentUser = auth.currentUser;
      if (!currentUser) {
        showStyledAlert('You must be logged in to delete activities', 'warning');
        return;
      }
      

      
      // Normalize: UI may pass step ids like "{parentId}_step_1". Always delete parent doc.
      const baseId = activityId && activityId.includes('_step_')
        ? activityId.split('_step_')[0]
        : activityId;

      // Delete the parent activity document
      await deleteDoc(doc(db, 'userActivities', baseId));
      console.log('✅ Activity deleted successfully');
      
      // Update local state
      // Remove both the parent and any step-keyed entries from local state
      setActivities(prev => prev.filter(activity => {
        if (!activity?.id) return true;
        if (activity.id === baseId) return false;
        if (activity.id.startsWith(baseId + '_step_')) return false;
        return true;
      }));
      
      // Show success message
      setNotificationMessage('Activity deleted successfully!');
      setShowNotification(true);
      setTimeout(() => setShowNotification(false), 3000);
      
      // Clear deleting state on success
      setDeletingActivityId(null);
      
    } catch (error) {
      console.error('❌ Error deleting activity:', error);
      
      // Handle specific error types
      if (error.code === 'permission-denied') {
        showStyledAlert('Permission denied. You can only delete your own activities.', 'error');
      } else if (error.code === 'unavailable' || error.code === 'deadline-exceeded') {
        if (retryCount < 3) {
          console.log(`🔄 Retrying delete operation (${retryCount + 1}/3)...`);
          // Wait a bit before retrying
          setTimeout(() => {
            handleDeleteActivity(activityId, retryCount + 1);
          }, 1000 * (retryCount + 1)); // Exponential backoff
        } else {
          showStyledAlert('Network error after multiple attempts. Please check your connection and try again.', 'error');
        }
      } else if (error.code === 'not-found') {
        showStyledAlert('Activity not found. It may have already been deleted.', 'info');
      } else {
        showStyledAlert(`Failed to delete activity: ${error.message}`, 'error');
      }
      // Clear deleting state on error (unless retrying)
      if (retryCount === 0) {
        setDeletingActivityId(null);
      }
    }
  };

  const handleWaysOfSeeing = (originalArtwork, title) => {
    setSelectedOriginalArtwork(originalArtwork);
    setSelectedOriginalArtworkTitle(title);
    setShowWaysOfSeeing(true);
  };

  const closeWaysOfSeeing = () => {
    setShowWaysOfSeeing(false);
    setSelectedOriginalArtwork(null);
    setSelectedOriginalArtworkTitle('');
  };

  // Helper function to get activity type label
  const getActivityTypeLabel = (type) => {
    switch (type) {
      case 'appreciation': return 'Art Appreciation';
      case 'story': return 'Art Story';
              case 'reimagine': return 'Ways of Seeing';
      case 'tutorial': return 'Tutorial';
      case 'daily_activity': return 'Daily Art Activity';
      default: return 'Activity';
    }
  };

  // Helper function to get activity type icon
  const getActivityTypeIcon = (type) => {
    switch (type) {
      case 'appreciation': return '🎨';
      case 'story': return '📚';
      case 'reimagine': return '✨';
      case 'tutorial': return '🎓';
      case 'daily_activity': return '🎨';
      default: return '📋';
    }
  };

  // Helper function to get activity type color
  const getActivityTypeColor = (type) => {
    switch (type) {
      case 'appreciation': return 'bg-purple-500';
      case 'story': return 'bg-blue-500';
      case 'reimagine': return 'bg-green-500';
      case 'tutorial': return 'bg-orange-500';
      case 'daily_activity': return 'bg-amber-500';
      default: return 'bg-gray-500';
    }
  };

  // Use centralized URL resolver for consistency
  const resolveImageUrl = resolveImageUrlUtil;

  const getExpandedImage = (activity) => {
    if (!activity) return '';
    if (activity.type === 'reimagine') {
      const j = Array.isArray(activity.journey) ? activity.journey : [];
      const last = j.length > 0 ? j[j.length - 1] : null;
      const url = (last?.transformedImageUrl || activity.transformedImageUrl || activity.originalImageUrl) || '';
      return resolveImageUrl(url);
    }
    return resolveImageUrl(activity.originalImageUrl || '');
  };

  // Convert local URLs to full backend URLs (use resolveImageUrl for consistency)
  const getFullImageUrl = (imageUrl) => {
    console.log('🔍 getFullImageUrl called with:', {
      imageUrl: imageUrl,
      type: typeof imageUrl,
      isString: typeof imageUrl === 'string',
      isNull: imageUrl === null,
      isUndefined: imageUrl === undefined
    });
    
    // Use resolveImageUrl which already handles production URLs correctly
    return resolveImageUrl(imageUrl);
  };

  // Check if tutorial exists for the current activity
  const checkTutorialExists = async (activity) => {
    if (!activity || activity.type !== 'reimagine') {
      console.log('❌ Invalid activity for tutorial check:', activity?.type);
      return false;
    }
    
    try {
      console.log('🔍 ActivityDashboard: Checking for existing tutorial data within reimagine activity:', {
        activityId: activity.id,
        styleId: activity.styleInfo?.id,
        styleName: activity.styleInfo?.name,
        hasTutorialData: !!activity.tutorialData,
        tutorialDataKeys: activity.tutorialData ? Object.keys(activity.tutorialData) : [],
        fullActivityKeys: Object.keys(activity)
      });
      
      // Check if the activity itself has tutorial data
      if (activity.tutorialData && activity.tutorialData.tutorial) {
        console.log('✅ ActivityDashboard: Found tutorial data within activity object');
        console.log('🔍 ActivityDashboard: Tutorial data details:', {
          hasTutorial: !!activity.tutorialData.tutorial,
          tutorialKeys: activity.tutorialData.tutorial ? Object.keys(activity.tutorialData.tutorial) : [],
          tutorialId: activity.tutorialData.tutorialId
        });
        return true;
      }
      
      // Use the new tutorial ID-based detection
      console.log('🔍 ActivityDashboard: Checking tutorial with IDs:', {
        styleId: activity.styleInfo?.id,
        artworkId: activity.id,
        activityId: activity.id,
        activityType: activity.type
      });
      
      const existingTutorial = await checkTutorialById(
        activity.styleInfo?.id,
        activity.id
      );
      
      const hasTutorial = !!existingTutorial;
      console.log('🔍 ActivityDashboard: Tutorial existence check result:', hasTutorial, existingTutorial ? `(ID: ${existingTutorial.id})` : '');
      
      if (existingTutorial) {
        console.log('🔍 ActivityDashboard: Found tutorial details:', {
          id: existingTutorial.id,
          tutorialId: existingTutorial.tutorialId,
          styleInfo: existingTutorial.styleInfo,
          createdAt: existingTutorial.createdAt
        });
      }
      
      return hasTutorial;
    } catch (error) {
      console.error('❌ ActivityDashboard: Error checking tutorial existence:', error);
      return false;
    }
  };

  // Handle reimagine card click - check for multiple transformations
  const handleReimagineCardClick = async (activity) => {
    if (!activity || activity.type !== 'reimagine') {
      console.log('❌ Invalid activity for reimagine card:', activity);
      return;
    }
    
    try {
      console.log('🎯 Starting reimagine card click process for activity:', activity.id);
      
      // Check if there are multiple transformations for this artwork
      const transformedActivities = await getTransformedArtworksForOriginal(activity.originalImageUrl, auth.currentUser.uid);
      const reimagineActivities = transformedActivities.filter(a => a.type === 'reimagine');
      
      console.log('🎯 Found reimagine activities:', reimagineActivities.length);
      
      if (reimagineActivities.length > 1) {
        // Multiple transformations exist - show navigation modal
        console.log('🎯 Multiple transformations found, showing navigation modal');
        setSelectedOriginalArtworkForNavigation(activity.originalImageUrl);
        setSelectedOriginalArtworkTitleForNavigation(activity.title || 'Your Artwork');
        setInitialActivityForNavigation(activity);
        setShowTransformationNavigation(true);
      } else {
        // Single transformation - show regular modal
        console.log('🎯 Single transformation found, showing regular modal');
        setSelectedActivity(activity);
        setShowDetailsModal(true);
      }
    } catch (error) {
      console.error('❌ Error handling reimagine card click:', error);
      // Fallback to regular modal
      setSelectedActivity(activity);
      setShowDetailsModal(true);
    }
  };

  // Handle tutorial button click
  const handleTutorialClick = async (activity) => {
    if (!activity || activity.type !== 'reimagine') {
      console.log('❌ Invalid activity for tutorial:', activity);
      return;
    }
    
    try {
      console.log('🎯 Starting tutorial process for activity:', activity.id);
      console.log('🎯 Activity styleInfo:', activity.styleInfo);
      console.log('🎯 Activity image:', getExpandedImage(activity));
      console.log('🎯 Full activity object:', activity);
      
      // Check for existing tutorial first
      const hasExistingTutorial = await checkTutorialExists(activity);
      
      if (hasExistingTutorial) {
        // Tutorial exists - show existing tutorial (no backend call needed)
        console.log('📘 Found existing tutorial, showing it without backend call');
        setSelectedActivity(activity); // Set selectedActivity for artworkId
        setTutorialStyleInfo(activity.styleInfo);
        setTutorialImage(getExpandedImage(activity));
        setTutorialKey(prev => prev + 1); // Force re-render
        // Open tutorial and ensure other overlays are closed
        setShowDetailsModal(false);
        setExpanded(false);
        setShowTransformationNavigation(false);
        setShowTutorialModal(true);
        setHasTutorial(true); // Mark as existing tutorial
        console.log('✅ Showing existing tutorial - NO BACKEND COST');
        
        // If the activity has tutorial data, pass it to avoid backend call
        if (activity.tutorialData && activity.tutorialData.tutorial) {
          console.log('✅ Activity has tutorial data, will use it directly');
        }
      } else {
        // No tutorial exists - generate new one
        console.log('📘 No existing tutorial found, will generate new one');
        
        // Set state immediately to show modal
        const tutorialImage = getExpandedImage(activity);
        console.log('🎯 Setting tutorial image:', tutorialImage);
        
        setSelectedActivity(activity); // Set the selected activity
        setTutorialStyleInfo(activity.styleInfo);
        setTutorialImage(tutorialImage);
        setTutorialKey(prev => prev + 1); // Force re-render
        // Open tutorial and ensure other overlays are closed
        setShowDetailsModal(false);
        setExpanded(false);
        setShowTransformationNavigation(false);
        setShowTutorialModal(true);
        setHasTutorial(false); // Mark as new tutorial
        
        console.log('✅ Modal state set for new tutorial generation');
        console.log('✅ showTutorialModal:', true);
        console.log('✅ tutorialStyleInfo:', activity.styleInfo);
        console.log('✅ tutorialImage:', tutorialImage);
        
        // Clear any cached tutorial for this style to force generation
        const cacheKey = `tutorial_${activity.styleInfo?.id || 'unknown'}_10`;
        localStorage.removeItem(cacheKey);
        console.log('🗑️ Cleared cached tutorial for key:', cacheKey);
      }
    } catch (error) {
      console.error('❌ Error handling tutorial click:', error);
      // Even if there's an error, keep the modal open so user can see the error
    }
  };

  useEffect(() => {
    if (expanded && selectedActivity) {
      try {
        const url = getExpandedImage(selectedActivity);
        console.log('🖼️ Expanded image URL:', url);
      } catch (e) {
        console.log('🖼️ Expanded image URL error:', e);
      }
    }
  }, [expanded, selectedActivity]);

  // Check if activity has tutorial when modal opens
  useEffect(() => {
    if (showDetailsModal && selectedActivity && selectedActivity.type === 'reimagine') {
      checkTutorialExists(selectedActivity).then(hasTutorial => {
        console.log('🔍 Tutorial existence check result:', hasTutorial);
        setHasTutorial(hasTutorial);
      });
    }
  }, [showDetailsModal, selectedActivity]);

  // Also check tutorial existence when selectedActivity changes
  useEffect(() => {
    if (selectedActivity && selectedActivity.type === 'reimagine') {
      checkTutorialExists(selectedActivity).then(hasTutorial => {
        console.log('🔍 Tutorial existence check for selectedActivity:', hasTutorial);
        setHasTutorial(hasTutorial);
      });
    }
  }, [selectedActivity]);

  // Debug useEffect to monitor tutorial modal state
  useEffect(() => {
    console.log('🔍 Tutorial modal state changed:', {
      showTutorialModal,
      hasTutorialStyleInfo: !!tutorialStyleInfo,
      hasTutorialImage: !!tutorialImage,
      tutorialKey
    });
  }, [showTutorialModal, tutorialStyleInfo, tutorialImage, tutorialKey]);

  useEffect(() => {
    if (!loading) setHasLoadedOnce(true);
  }, [loading]);

  // No longer needed - StepByStepLearning component handles its own loading state

  // Clean up separate tutorial activities on component mount
  useEffect(() => {
    const cleanup = async () => {
      try {
        await cleanupTutorialActivities();
      } catch (error) {
        console.error('❌ Error during cleanup:', error);
      }
    };
    
    cleanup();
  }, []);

  if (loading && !hasLoadedOnce && (!activities || activities.length === 0)) {
    return <ConsistentLoader message="Loading your activities..." />;
  }

  // Render the main content based on current view
  let mainContent;
  const storyActivities = activities.filter(activity => activity.type === 'story');
  
  // Magic Studio View - Curated by Artwork
  if (currentView === 'magic-studio') {
    mainContent = (
      <div className="artm-page artm-page-pad min-h-screen relative bg-[#FFF8E1] flex justify-center px-0 py-0 md:px-[2.5cm] md:py-8 lg:py-10">
        {/* Amber color splashes background - matching home page */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-10 w-72 h-72 bg-amber-200/25 rounded-full blur-3xl"></div>
          <div className="absolute bottom-40 left-10 w-96 h-96 bg-amber-300/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 right-1/4 w-56 h-56 bg-amber-400/22 rounded-full blur-3xl"></div>
          <div className="absolute top-1/3 left-1/3 w-40 h-40 bg-amber-200/15 rounded-full blur-2xl"></div>
          <div className="absolute bottom-1/4 right-1/3 w-48 h-48 bg-amber-300/18 rounded-full blur-3xl"></div>
        </div>
        
        {/* Splash images merged into background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-5">
          <SmartImage
            src={splashImage}
            alt=""
            className="absolute top-10 right-5 opacity-20"
            style={{
              width: '400px',
              height: '200px',
              objectFit: 'cover',
              transform: 'rotate(15deg)',
              mixBlendMode: 'multiply',
              filter: 'blur(2px)'
            }}
            loading="eager"
            fetchPriority="high"
          />
          <SmartImage
            src={splashImage}
            alt=""
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-15"
            style={{
              width: '300px',
              height: '150px',
              objectFit: 'cover',
              transform: 'translate(-50%, -50%) rotate(10deg)',
              mixBlendMode: 'multiply',
              filter: 'blur(3px)'
            }}
            loading="eager"
            fetchPriority="auto"
          />
        </div>
        <KidBackground variant="blobs" intensity={1} />
        
        {/* Black overlay when any modal/overlay is open - Desktop: show overlay for expanded, Mobile: expanded modal handles its own background */}
        {(showDetailsModal || (expanded && !isMobile) || (!expanded && (showTutorialModal || showTransformationNavigation || showWaysOfSeeing))) && (
          <div 
            className="fixed inset-0 bg-black z-[9998]" 
            style={{ zIndex: 9998, opacity: expanded && !isMobile ? 0.5 : 0.8 }}
            onClick={(e) => {
              e.stopPropagation();
            }}
          ></div>
        )}
        
        <div className="relative z-10 w-full max-w-full py-0 md:py-4">
          <div className="bg-white/95 rounded-none md:rounded-[32px] shadow-2xl border-0 md:border-2 border-amber-300 p-3 md:p-6 flex flex-col overflow-visible">
            {/* Compact header — Art History Learning style */}
            <header className="flex-shrink-0 sticky top-0 z-30 bg-white/95 backdrop-blur-sm border-b border-amber-200/50 shadow-sm max-h-[72px] md:max-h-[80px]">
              <div className="flex items-center justify-between py-2 md:py-2.5 h-full min-h-[52px]">
                <p className="text-sm md:text-base font-semibold text-gray-800">
                  Magic Studio Activity Dashboard
                </p>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  <button
                    type="button"
                    onClick={(e) => goToArtistCorner(navigate, e)}
                    className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all flex items-center justify-center"
                    title="Artist Corner"
                  >
                    <LayoutDashboard className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                    <span className="hidden md:inline ml-1">Artist Corner</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => navigate('/home')}
                    className="p-1.5 md:px-3 md:py-1.5 bg-amber-50/80 text-amber-800 text-xs font-medium rounded-full border border-amber-200/60 shadow-sm hover:bg-amber-100/80 transition-all flex items-center justify-center"
                    title="Home"
                  >
                    <Home className="w-3.5 h-3.5 md:w-4 md:h-4 flex-shrink-0" />
                    <span className="hidden md:inline ml-1">Home</span>
                  </button>
                </div>
              </div>
            </header>

        {/* Intro line */}
        <p className="text-sm text-slate-600 mb-4 -mt-1">
          Explore creative activities performed on your artworks.
        </p>

        {/* Centered Toast */}
        {showNotification && (
          <div className="fixed inset-0 z-[9998] pointer-events-none flex items-center justify-center">
            <div className="pointer-events-auto bg-white/95 backdrop-blur-sm border-2 border-amber-300 rounded-2xl shadow-2xl px-6 py-4 text-center">
              <p className="text-amber-800 font-semibold">{notificationMessage}</p>
            </div>
          </div>
        )}

          {/* Artworks Grid */}
          {groupedArtworks.length === 0 ? (
          <div className="text-center py-12">
              <h3 className="text-2xl font-semibold text-slate-900 mb-2">No artworks yet</h3>
              <p className="text-slate-700 mb-6">Upload artworks and start creating to see them here!</p>
            <button
              onClick={() => navigate('/artist-wall')}
              className="art-btn-primary rounded-full px-6 py-3 font-semibold transition"
            >
                Go to Artist Wall
            </button>
          </div>
        ) : (
            <div className="mt-4 rounded-2xl border border-amber-100/70 bg-white/50 backdrop-blur-sm shadow-md">
              <div className="max-h-[70vh] overflow-y-auto px-2 md:px-4 sm:px-6 pt-4 md:pt-6 pb-4 md:pb-8">
                <div className="space-y-6 md:space-y-8">
                  {groupedArtworks
                .filter(artworkGroup => {
                  const hasOnlyArtHistory = artworkGroup.activities?.every(activity => {
                    const isAppreciation = activity.type === 'art-history-appreciation' || 
                                          activity.metadata?.feature === 'art-history-appreciation' ||
                                          activity.feature === 'art-history-appreciation';
                    const isComparison = activity.type === 'art-comparison' || 
                                       activity.metadata?.feature === 'art-comparison' ||
                                       activity.feature === 'art-comparison';
                    return isAppreciation || isComparison;
                  }) && artworkGroup.activities?.length > 0;
                  const isArtHistoryGroup = artworkGroup.artworkUrl === 'art-history-appreciation-all' || artworkGroup.artworkUrl === 'art-comparison-all';
                  return !hasOnlyArtHistory && !isArtHistoryGroup;
                })
                .map((artworkGroup, index) => (
                <div key={index} className="bg-white/90 backdrop-blur-sm rounded-2xl shadow-lg overflow-hidden relative border-2 border-amber-200">
                  <div className="absolute -top-3 left-0 right-0 bg-gradient-to-r from-amber-400 via-orange-400 to-yellow-400 text-white py-1.5 md:py-2 text-center text-xs md:text-sm font-bold shadow-lg z-20 border-2 border-amber-300">
                    Activities of &quot;{artworkGroup.originalTitle || artworkGroup.title || 'Untitled Artwork'}&quot;
                  </div>
                  
                  <div className="flex flex-col md:flex-row">
                    <div className="w-full md:w-1/3 p-3 md:p-6">
                      <div className="relative h-48 md:h-64 rounded-xl overflow-hidden border-2 md:border-4 border-amber-200">
                        <SmartImage
                          src={getFullImageUrl(artworkGroup.artworkUrl)}
                          alt="Original artwork"
                          className="w-full h-full object-contain md:object-cover"
                          containerClassName="w-full h-full"
                          style={{ objectPosition: 'center' }}
                          onError={(e) => {
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.parentElement?.querySelector('.artwork-fallback');
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        <div className="artwork-fallback hidden absolute inset-0 items-center justify-center bg-gradient-to-br from-amber-50 to-orange-50">
                          <div className="text-center">
                            <div className="text-4xl mb-2">🎨</div>
                            <p className="text-sm text-amber-600 font-medium">Original Artwork</p>
                            <p className="text-xs text-amber-500 mt-1">{artworkGroup.originalTitle || artworkGroup.title || 'Untitled'}</p>
                          </div>
                        </div>
                        <div className="absolute bottom-2 left-2 bg-white/90 px-3 py-1 rounded text-sm font-semibold text-amber-700">
                          Original Artwork
                        </div>
                      </div>
                    </div>

                    <div className="w-full md:w-2/3 p-3 md:p-6 pt-6 md:pt-8 flex flex-col justify-center min-h-[200px] md:min-h-[280px]">
                      <div className="relative">
                        <div className="text-xs text-slate-600 mb-2 text-center md:hidden">← Swipe to see more activities →</div>
                        <div className="overflow-x-auto overflow-y-hidden scrollbar-hide activities-scroll -mx-2 md:mx-0" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                          <div className="flex gap-3 px-2 md:px-0" style={{ minWidth: 'max-content' }}>
                        {(() => {
                          const waysOfSeeingActivities = dedupeWaysOfSeeingActivitiesByArtworkStyle(
                            artworkGroup.activities?.filter(activity => 
                            activity.type === 'reimagine' && 
                            activity.metadata?.feature !== 'reimagine-with-feedback' &&
                            activity.metadata?.feature !== 'reimagine-artwork' &&
                            activity.metadata?.feature !== 'reimagine' &&
                            (activity.metadata?.feature === 'ways-of-seeing-journey' || !activity.metadata?.feature)
                          ) || []
                          );
                          const rawReimaginedArtworkActivities = artworkGroup.activities?.filter(activity => {
                            if (activity.type !== 'reimagine') return false;
                            const feature = activity.metadata?.feature;
                            const shouldInclude = (feature === 'reimagine-artwork' || feature === 'reimagine' || !feature || feature === undefined || feature === null);
                            const shouldExclude = (feature === 'ways-of-seeing-journey' || feature === 'reimagine-with-feedback');
                            return shouldInclude && !shouldExclude;
                          }) || [];
                          let reimaginedArtworkActivities = [];
                          if (rawReimaginedArtworkActivities.length > 0) {
                            const canonical = rawReimaginedArtworkActivities.reduce((latest, current) => {
                              const lt = latest?.createdAt?.toDate ? latest.createdAt.toDate().getTime() : new Date(latest?.createdAt || 0).getTime();
                              const ct = current?.createdAt?.toDate ? current.createdAt.toDate().getTime() : new Date(current?.createdAt || 0).getTime();
                              return ct > lt ? current : latest;
                            }, rawReimaginedArtworkActivities[0]);
                            reimaginedArtworkActivities = [canonical];
                          }
                          const reimagineFeedbackActivities = artworkGroup.activities?.filter(activity => {
                            if (activity.type !== 'reimagine') return false;
                            if (activity.metadata?.artmosphereFeedback && Array.isArray(activity.metadata.artmosphereFeedback) && activity.metadata.artmosphereFeedback.length > 0) return true;
                            if (activity.metadata?.transformations && Array.isArray(activity.metadata.transformations)) {
                              return activity.metadata.transformations.some(t => t.artmosphereFeedback && Array.isArray(t.artmosphereFeedback) && t.artmosphereFeedback.length > 0);
                            }
                            return false;
                          }) || [];
                          const otherActivities = artworkGroup.activities?.filter(activity => activity.type !== 'reimagine') || [];
                          return (
                            <>
                              {waysOfSeeingActivities.length > 0 && (
                                <div className="flex-shrink-0 w-[280px] md:w-[300px]">
                                  <ReimagineScrollerCard
                                    key={`ways-of-seeing-${artworkGroup.artworkUrl}`}
                                    originalArtworkUrl={artworkGroup.artworkUrl}
                                    originalArtworkTitle={artworkGroup.title}
                                    waysOfSeeingActivities={waysOfSeeingActivities}
                                    onTutorialClick={(activity) => handleTutorialClick(activity)}
                                    onCardClick={(activity) => handleReimagineCardClick(activity)}
                                    onDeleteActivity={(activityId) => handleDeleteActivity(activityId)}
                                  />
                                </div>
                              )}
                              {reimagineFeedbackActivities.length === 0 && reimaginedArtworkActivities.length > 0 && (
                                <div className="flex-shrink-0 w-[280px] md:w-[300px]">
                                  <ReimaginedArtworkCard
                                    key={`reimagined-artwork-${artworkGroup.artworkUrl}`}
                                    originalArtworkUrl={artworkGroup.artworkUrl}
                                    originalArtworkTitle={artworkGroup.title}
                                    onTutorialClick={(activity) => handleTutorialClick(activity)}
                                    onCardClick={(activity) => handleReimagineCardClick(activity)}
                                    onDeleteActivity={(activityId) => handleDeleteActivity(activityId)}
                                  />
                                </div>
                              )}
                              {reimagineFeedbackActivities.length > 0 && (
                                <div className="flex-shrink-0 w-[280px] md:w-[300px]">
                                  <ReimagineFeedbackScrollerCard
                                    key={`feedback-${artworkGroup.artworkUrl}`}
                                    originalArtworkUrl={artworkGroup.artworkUrl}
                                    originalArtworkTitle={artworkGroup.title}
                                    reimagineActivities={reimagineFeedbackActivities}
                                    onDeleteActivity={(activityId) => handleDeleteActivity(activityId)}
                                  />
                                </div>
                              )}
                              {otherActivities.map((activity) => (
                                <div key={activity.id}>
                                  {activity.type === 'appreciation' ? (
                                    <div
                                      className="flex-shrink-0 w-[280px] md:w-[300px] bg-gradient-to-br from-orange-100 via-yellow-100 to-pink-100 rounded-xl p-3 cursor-pointer hover:shadow-lg transition-all duration-300 border-2 border-orange-300 hover:border-orange-400 relative overflow-hidden"
                                      onClick={() => openAppreciationDetails(activity)}
                                    >
                                      <div className="absolute inset-0 opacity-10">
                                        <div className="absolute top-2 right-2 text-4xl rotate-12">🎨</div>
                                        <div className="absolute bottom-2 left-2 text-3xl -rotate-12">✨</div>
                                        <div className="absolute top-1/2 left-1/2 text-2xl transform -translate-x-1/2 -translate-y-1/2 rotate-45">🌈</div>
                                      </div>
                                      <div className="absolute -top-1 left-4 bg-gradient-to-r from-orange-400 to-pink-400 text-white px-2 py-0.5 rounded-b text-xs font-bold shadow-md z-20">Art Appreciation</div>
                                      <div className="relative mb-2 z-10 mt-3">
                                        <div className="relative h-28 w-full rounded-2xl overflow-hidden border-2 border-orange-300 bg-gradient-to-br from-yellow-50 to-orange-50 shadow-lg">
                                          <SmartImage src={getExpandedImage(activity)} alt="Artwork for appreciation" className="w-full h-full object-cover" containerClassName="w-full h-full" onError={(e) => { e.currentTarget.style.display = 'none'; const fallback = e.currentTarget.parentElement?.querySelector('.image-fallback'); if (fallback) fallback.style.display = 'flex'; }} />
                                          <div className="image-fallback hidden absolute inset-0 items-center justify-center bg-gradient-to-br from-yellow-50 to-orange-50">
                                            <div className="text-center"><div className="text-4xl mb-2">🎨</div><p className="text-sm text-orange-600 font-medium">Art Appreciation</p></div>
                                          </div>
                                          <AppreciationHotspotPreview count={activity.aiPoints?.length || 4} />
                                          <button onClick={(e) => { e.stopPropagation(); handleDeleteActivity(activity.id); }} className="absolute bottom-1 right-1 text-red-400 hover:text-red-600 p-1 rounded-full hover:bg-red-100 transition-all duration-200 opacity-70 hover:opacity-100 z-40" title="Delete this creation"><FaTrash size={8} /></button>
                                        </div>
                                      </div>
                                      <div className="flex justify-between items-center mb-1 relative z-10">
                                        <div className="text-center flex-1"><p className="text-xs text-orange-700 font-medium">{formatActivityDate(activity, { includeTime: true })}</p></div>
                                      </div>
                                      <div className="relative z-10 mt-1">
                                        <button onClick={(e) => { e.stopPropagation(); openAppreciationDetails(activity); }} className="w-full bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105" style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}>View details</button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div
                                      className="flex-shrink-0 w-[280px] md:w-[300px] bg-gradient-to-br from-orange-100 via-yellow-100 to-pink-100 rounded-xl p-3 cursor-pointer hover:shadow-lg transition-all duration-300 border-2 border-orange-300 hover:border-orange-400 relative overflow-hidden"
                                      onClick={() => { setSelectedActivity(activity); setShowDetailsModal(true); }}
                                    >
                                      <div className="absolute inset-0 opacity-10">
                                        <div className="absolute top-2 right-2 text-4xl rotate-12">🎨</div>
                                        <div className="absolute bottom-2 left-2 text-3xl -rotate-12">✨</div>
                                        <div className="absolute top-1/2 left-1/2 text-2xl transform -translate-x-1/2 -translate-y-1/2 rotate-45">🌈</div>
                                      </div>
                                      <div className="absolute -top-1 left-4 bg-gradient-to-r from-orange-400 to-pink-400 text-white px-2 py-0.5 rounded-b text-xs font-bold shadow-md z-20">{getActivityTypeLabel(activity.type)}</div>
                                      <div className="relative mb-2 z-10 mt-3">
                                        <div className="relative h-28 w-full rounded-2xl overflow-hidden border-2 border-orange-300 bg-gradient-to-br from-yellow-50 to-orange-50 shadow-lg">
                                          <SmartImage src={getExpandedImage(activity)} alt={`${activity.title || 'Activity'} artwork`} className="w-full h-full object-cover" containerClassName="w-full h-full" onError={(e) => { e.currentTarget.style.display = 'none'; const fallback = e.currentTarget.parentElement?.querySelector('.image-fallback'); if (fallback) fallback.style.display = 'flex'; }} />
                                          <div className="image-fallback hidden absolute inset-0 items-center justify-center bg-gradient-to-br from-yellow-50 to-orange-50">
                                            <div className="text-center"><div className="text-4xl mb-2">{getActivityTypeIcon(activity.type)}</div><p className="text-sm text-orange-600 font-medium">{activity.title || getActivityTypeLabel(activity.type)}</p></div>
                                          </div>
                                          <div className="absolute bottom-1 left-1 bg-gradient-to-r from-orange-500 to-pink-500 text-white px-2 py-1 rounded text-xs font-bold shadow-md">{activity.title || getActivityTypeLabel(activity.type)}</div>
                                          <button onClick={(e) => { e.stopPropagation(); handleDeleteActivity(activity.id); }} className="absolute bottom-1 right-1 text-red-400 hover:text-red-600 p-1 rounded-full hover:bg-red-100 transition-all duration-200 opacity-70 hover:opacity-100 z-30" title="Delete this creation"><FaTrash size={8} /></button>
                                        </div>
                                      </div>
                                      <div className="flex justify-between items-center mb-1 relative z-10">
                                        <div className="text-center flex-1"><p className="text-xs text-orange-700 font-medium">{formatActivityDate(activity, { includeTime: true })}</p></div>
                                      </div>
                                      <div className="relative z-10 mt-1">
                                        <button onClick={(e) => { e.stopPropagation(); setSelectedActivity(activity); setShowDetailsModal(true); }} className="w-full bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 text-white px-3 py-2 rounded-lg text-xs font-semibold transition-all duration-300 shadow-md hover:shadow-lg transform hover:scale-105" style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}>View details</button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </>
                          );
                        })()}
                            </div>
                          </div>
                          <button
                            className="absolute right-0 top-1/2 -translate-y-1/2 z-10 bg-white/90 backdrop-blur-sm rounded-full p-2 shadow-lg border border-amber-200 hover:bg-white transition-all md:hidden"
                            onClick={(e) => {
                              e.stopPropagation();
                              const container = e.currentTarget.closest('.relative')?.querySelector('.activities-scroll');
                              if (container) container.scrollBy({ left: 300, behavior: 'smooth' });
                            }}
                            id={`scroll-right-${index}`}
                          >
                            <ChevronRight className="w-4 h-4 text-amber-700" />
                          </button>
                        </div>
                      </div>

                      <div className="mt-4 flex justify-end">
                        <button
                          onClick={async () => {
                            const confirmed = await new Promise((resolve) => {
                              const container = document.createElement('div');
                              container.className = 'fixed inset-0 z-[999999] flex items-center justify-center';
                              container.style.cssText = 'padding-top: env(safe-area-inset-top, 0px); padding-left: env(safe-area-inset-left, 0px); padding-right: env(safe-area-inset-right, 0px); padding-bottom: env(safe-area-inset-bottom, 0px);';
                              container.innerHTML = `
                                <div class="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"></div>
                                <div class="relative bg-white rounded-2xl p-6 w-full max-w-md mx-4 shadow-2xl border-2 border-amber-200" style="font-family: 'Baloo 2', 'Comic Sans MS', cursive;">
                                  <h3 class="text-2xl font-bold text-amber-800 mb-2" style="font-family: 'Baloo 2', 'Comic Sans MS', cursive;">Delete all activities?</h3>
                                  <p class="text-amber-700 mb-4" style="font-family: 'Baloo 2', 'Comic Sans MS', cursive;">This action cannot be undone.</p>
                                  <div class="flex gap-3 justify-end">
                                    <button id="am-cancel" class="px-4 py-2 rounded-lg bg-gray-200 text-gray-700 hover:bg-gray-300 transition-all duration-200 font-semibold" style="font-family: 'Baloo 2', 'Comic Sans MS', cursive;">Cancel</button>
                                    <button id="am-ok" class="px-4 py-2 rounded-lg art-btn-primary transition-all duration-200 font-semibold" style="font-family: 'Baloo 2', 'Comic Sans MS', cursive;">Delete All</button>
                                  </div>
                                </div>`;
                              document.body.appendChild(container);
                              const cleanup = () => { if (document.body.contains(container)) document.body.removeChild(container); };
                              container.querySelector('#am-cancel')?.addEventListener('click', () => { cleanup(); resolve(false); });
                              container.querySelector('#am-ok')?.addEventListener('click', () => { cleanup(); resolve(true); });
                              container.querySelector('.absolute.inset-0')?.addEventListener('click', () => { cleanup(); resolve(false); });
                            });
                            if (confirmed) {
                              try {
                                await Promise.all(artworkGroup.activities.map(activity => handleDeleteActivity(activity.id, 0, true)));
                                setNotificationMessage('All activities deleted successfully!');
                                setShowNotification(true);
                                setTimeout(() => setShowNotification(false), 3000);
                              } catch (error) {
                                console.error('Error deleting activities:', error);
                                showStyledAlert('Some activities could not be deleted. Please try again.', 'error');
                              }
                            }
                          }}
                          className="bg-gradient-to-r from-red-300/50 to-orange-300/50 hover:from-red-400/70 hover:to-orange-400/70 text-red-700 hover:text-red-800 px-4 py-2 rounded-full transition-all duration-300 shadow-sm hover:shadow-md transform hover:scale-105 opacity-50 hover:opacity-80 text-sm"
                        >
                          🗑️
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Once Upon a Click View - Stories Only
  else if (currentView === 'once-upon-click') {
    mainContent = null;
  }

  // Daily Acts View - Daily Activities Only
  else if (currentView === 'daily-acts') {
    const dailyActivities = activities.filter(activity => activity.type === 'daily_activity');
    
    mainContent = (
      <div className="artm-page artm-page-pad min-h-screen relative bg-[#FFF8E1]">
        {/* Amber color splashes background - matching home page */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-10 w-72 h-72 bg-amber-200/25 rounded-full blur-3xl"></div>
          <div className="absolute bottom-40 left-10 w-96 h-96 bg-amber-300/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/2 right-1/4 w-56 h-56 bg-amber-400/22 rounded-full blur-3xl"></div>
          <div className="absolute top-1/3 left-1/3 w-40 h-40 bg-amber-200/15 rounded-full blur-2xl"></div>
          <div className="absolute bottom-1/4 right-1/3 w-48 h-48 bg-amber-300/18 rounded-full blur-3xl"></div>
        </div>
        
        {/* Splash images merged into background */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-5">
          <SmartImage
            src={splashImage}
            alt=""
            className="absolute top-10 right-5 opacity-20"
            style={{
              width: '400px',
              height: '200px',
              objectFit: 'cover',
              transform: 'rotate(15deg)',
              mixBlendMode: 'multiply',
              filter: 'blur(2px)'
            }}
            loading="eager"
            fetchPriority="high"
          />
          <SmartImage
            src={splashImage}
            alt=""
            className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 opacity-15"
            style={{
              width: '300px',
              height: '150px',
              objectFit: 'cover',
              transform: 'translate(-50%, -50%) rotate(10deg)',
              mixBlendMode: 'multiply',
              filter: 'blur(3px)'
            }}
            loading="eager"
            fetchPriority="auto"
          />
        </div>
        <KidBackground variant="blobs" intensity={1} />
        
        {/* Black overlay when any modal/overlay is open - Desktop: show overlay for expanded, Mobile: expanded modal handles its own background */}
        {(showDetailsModal || (expanded && !isMobile) || (!expanded && (showTutorialModal || showTransformationNavigation || showWaysOfSeeing))) && (
          <div 
            className="fixed inset-0 bg-black z-[9998]" 
            style={{ zIndex: 9998, opacity: expanded && !isMobile ? 0.5 : 0.8 }}
            onClick={(e) => {
              // Prevent clicks from passing through overlay
              e.stopPropagation();
            }}
          ></div>
        )}
        
        <div className="relative z-10 max-w-7xl mx-auto p-3 md:p-6">
          {/* Brand + Title */}
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-6 md:mb-8 gap-3 md:gap-0">
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center w-6 h-6 md:w-8 md:h-8 rounded-full bg-gradient-to-br from-amber-300 to-orange-300 text-black font-bold shadow-sm text-xs md:text-base">A</span>
              <span className="text-slate-900 font-semibold text-sm md:text-lg tracking-wide">Artmosphere</span>
            </div>
            <h1 className="text-xl md:text-2xl lg:text-3xl font-bold text-slate-900">My Daily Acts of Art</h1>
            <div />
          </div>

          {/* Daily Activities Grid */}
          {dailyActivities.length === 0 ? (
            <div className="text-center py-12">
              <h3 className="text-2xl font-semibold text-slate-900 mb-2">No daily activities completed yet</h3>
              <p className="text-slate-700 mb-6">Complete your daily art activities to see them here!</p>
              <button
                onClick={() => navigate('/daily-activities')}
                className="art-btn-primary rounded-full px-6 py-3 font-semibold transition"
              >
                Go to Daily Acts
              </button>
            </div>
          ) : (
            <div className="mt-4 rounded-2xl border border-amber-100/70 bg-white/50 backdrop-blur-sm shadow-md">
              <div className="max-h-[70vh] overflow-y-auto px-4 sm:px-6 pt-6 pb-8">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {dailyActivities.map((activity) => (
                <div
                  key={activity.id}
                  onClick={() => {
                    setSelectedActivity(activity);
                    setShowDetailsModal(true);
                  }}
                  className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer border-4 border-amber-200 hover:border-amber-300"
                >
                  {/* Card Header with Activity ID and Type */}
                  <div className="p-4 border-b border-amber-200">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-3">
                        <div className="bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                          #{activity.activityId || 'N/A'}
                        </div>
                        <div className="bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                          🎨 Daily
                        </div>
                      </div>
                      <span className="text-xs text-amber-600">
                        {formatActivityDate(activity, { includeTime: true })}
                      </span>
                    </div>
                  </div>
                  
                  {/* Main Content - Two Column Layout */}
                  <div className="p-6">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                      {/* Left Column - Activity Details */}
                      <div className="activity-details">
                        <h3 className="text-lg font-bold text-amber-800 mb-3">
                          {activity.title || activity.activityTitle || `Daily Activity ${activity.activityId}`}
                        </h3>
                        <p className="text-amber-700 text-sm mb-4 leading-relaxed">
                          {activity.description || activity.activityDescription || "Daily creative activity for developing artistic thinking"}
                        </p>
                        
                        {/* Metadata if available */}
                        {activity.metadata && (
                          <div className="space-y-2 text-sm">
                            {activity.metadata.prerequisites && (
                              <div className="flex items-start gap-2">
                                <span className="text-amber-600 font-medium min-w-[80px]">Materials:</span>
                                <span className="text-amber-700">{activity.metadata.prerequisites}</span>
                              </div>
                            )}
                            {activity.metadata.steps && (
                              <div className="flex items-start gap-2">
                                <span className="text-amber-600 font-medium min-w-[80px]">Steps:</span>
                                <span className="text-amber-700">{activity.metadata.steps}</span>
                              </div>
                            )}
                          </div>
                        )}
                        
                        {/* Status Indicators */}
                        <div className="mt-4 space-y-2">
                          {(activity.transformedImageUrl || activity.userArtworkUrl) && (
                            <div className="flex items-center gap-2 text-green-700 text-sm">
                              <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                              <span className="font-medium">Artwork Submitted</span>
                            </div>
                          )}
                          
                          {activity.feedback && (
                            <div className="flex items-center gap-2 text-blue-700 text-sm">
                              <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                              <span className="font-medium">AI Feedback Available</span>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Right Column - User's Artwork */}
                      <div className="user-artwork">
                        <h4 className="font-semibold text-amber-800 mb-3 text-center">Your Artwork</h4>
                        <div className="relative w-full h-48 rounded-xl border-2 border-amber-200 shadow-md overflow-hidden bg-amber-50">
                          <SmartImage
                            src={resolveImageUrl(activity.transformedImageUrl || activity.userArtworkUrl || activity.originalImageUrl || activity.activityPageUrl || '/static/media/create-art.png')}
                            alt="Your artwork"
                            className="w-full h-full object-cover"
                            containerClassName="w-full h-full"
                            onError={(e) => {
                              console.error('🖼️ Failed to load user artwork:', e.target.src);
                              e.currentTarget.style.display = 'none';
                              const fallback = e.currentTarget.parentElement.querySelector('.image-fallback');
                              if (fallback) fallback.style.display = 'flex';
                            }}
                          />
                          {/* Fallback when image fails to load */}
                          <div className="image-fallback hidden absolute inset-0 items-center justify-center bg-amber-50">
                            <div className="text-center">
                              <div className="text-4xl mb-2">🎨</div>
                              <p className="text-sm font-medium text-amber-700">Your Artwork</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    {/* Artmosphere Feedback Section */}
                    {activity.feedback && (
                      <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-4 border-2 border-amber-200">
                        <h4 className="font-semibold text-amber-800 mb-3 flex items-center gap-2">
                          <span>✨</span>
                          Artmosphere Feedback
                        </h4>
                        
                        {/* Overall Message */}
                        {activity.feedback.overallMessage && (
                          <div className="mb-3 p-3 bg-white rounded-lg border border-amber-200">
                            <h5 className="font-medium text-amber-800 mb-2 text-sm">Overall Message</h5>
                            <p className="text-amber-700 text-sm leading-relaxed">{activity.feedback.overallMessage}</p>
                          </div>
                        )}
                        
                        {/* Strengths and Improvements in a compact format */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {/* Strengths */}
                          {activity.feedback.strengths && activity.feedback.strengths.length > 0 && (
                            <div className="p-3 bg-white rounded-lg border border-green-200">
                              <h5 className="font-medium text-green-800 mb-2 text-sm">✨ Strengths</h5>
                              <ul className="list-disc list-inside text-green-700 text-xs space-y-1">
                                {activity.feedback.strengths.slice(0, 2).map((strength, index) => (
                                  <li key={index}>{strength}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* Improvements */}
                          {activity.feedback.improvements && activity.feedback.improvements.length > 0 && (
                            <div className="p-3 bg-white rounded-lg border border-blue-200">
                              <h5 className="font-medium text-blue-800 mb-2 text-sm">🚀 Areas to Explore</h5>
                              <ul className="list-disc list-inside text-blue-700 text-xs space-y-1">
                                {activity.feedback.improvements.slice(0, 2).map((improvement, index) => (
                                  <li key={index}>{improvement}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  
                  {/* Card Footer with Actions */}
                  <div className="px-6 py-4 border-t border-amber-200 bg-amber-50">
                    <div className="flex justify-between items-center">
                      <div className="text-xs text-amber-600">
                        Click to view full details
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteActivity(activity.id);
                        }}
                        className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-lg transition-colors"
                        title="Delete Activity"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Default content (should never reach here)
  else {
    mainContent = (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 via-amber-100 to-amber-200">
        <div className="max-w-7xl mx-auto p-6">
          <div className="text-center py-12">
            <div className="text-6xl mb-4">🎨</div>
            <h3 className="text-2xl font-semibold text-amber-800 mb-2">Loading...</h3>
          </div>
        </div>
      </div>
    );
  }

  // Return the main content with modals
  console.log('🔍 [RENDER] Component rendering - expanded:', expanded, 'showAppreciationModal:', showAppreciationModal, 'selectedAppreciationActivity:', !!selectedAppreciationActivity);
  
  return (
    <>
      {/* Hide main content when expanded - Desktop: overlay style, Mobile: completely hidden */}
      <div 
        style={{
          display: expanded ? 'none' : 'block',
          visibility: expanded ? 'hidden' : 'visible',
          opacity: expanded ? 0 : 1,
          pointerEvents: expanded ? 'none' : 'auto',
          position: expanded ? 'fixed' : 'relative',
          zIndex: expanded ? -1 : 'auto',
          height: expanded ? 0 : 'auto',
          overflow: expanded ? 'hidden' : 'visible'
        }}
      >
        {console.log('🔍 [MAIN CONTENT] Rendering main content wrapper - expanded:', expanded)}
        {mainContent}
      </div>
      
      {/* Activity Details Modal - only for non-reimagine activities to avoid duplication - Hide when expanded */}
      {console.log('🎨 RENDERING MODAL CHECK - showDetailsModal:', showDetailsModal, 'selectedActivity:', !!selectedActivity)}
        {showDetailsModal && !expanded && selectedActivity && selectedActivity.type !== 'reimagine' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" style={{ 
          zIndex: 9999,
          paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))',
          paddingLeft: 'env(safe-area-inset-left, 0px)',
          paddingRight: 'env(safe-area-inset-right, 0px)',
          paddingBottom: 'env(safe-area-inset-bottom, 0px)'
        }}>
            <div className="bg-white rounded-xl max-w-7xl w-full max-h-[95vh] overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
                    <h2 className="text-2xl font-bold text-amber-800">
                {getActivityTypeLabel(selectedActivity.type)} - {selectedActivity.title}
                    </h2>
                <div className="flex items-center gap-4">
                  {/* Transformation Counter */}
                  <div className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-semibold">
                    1/2
                  </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDetailsModal(false);
                    setSelectedActivity(null);
                    setExpanded(false);
                    setVisibleBoxIndex(null);
                    setShowTipBox(false);
                    setShowTechniques(false);
                    setShowTechniquePopup(false);
                    setShowTutorialModal(false);
                    setTutorialStyleInfo(null);
                    setTutorialImage(null);
                    console.log('✅ Details modal closed, all states cleared');
                  }}
                className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                ✕
                </button>
                </div>
              </div>

            {/* Content */}
            {selectedActivity.type === 'reimagine' ? (
              <div className="p-6">
                <div className="space-y-6">
                {/* Results Grid */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                  {/* Original */}
                  <div className="original-result">
                    <h4 className="font-semibold text-amber-800 mb-3 text-center">Original Artwork</h4>
                    <div className="relative w-full h-[400px] rounded-2xl border-4 border-amber-200 shadow-lg overflow-hidden bg-white/90 backdrop-blur-sm">
                      <SmartImage
                        src={getFullImageUrl(selectedActivity.originalImageUrl)}
                        alt="Original artwork"
                        className="w-full h-full object-cover"
                        containerClassName="w-full h-full"
                        onError={(e) => console.error('🖼️ Failed to load original image:', e.currentTarget.src)}
                      />
                    <div className="absolute bottom-2 left-2 text-center text-amber-700 font-semibold bg-white/80 px-2 py-1 rounded">
                        Original
                      </div>
                    </div>
                    </div>
                    
                  {/* Transformed */}
                  <div className="transformed-result">
                    <h4 className="font-semibold text-amber-800 mb-3 text-center">
                      {selectedActivity?.styleInfo?.name || 'Transformed'} Style
                    </h4>
                    <div className="relative w-full h-[400px] rounded-2xl border-4 border-amber-200 shadow-lg overflow-hidden bg-white/90 backdrop-blur-sm cursor-pointer hover:scale-105 transition-transform duration-300 group"
                      onClick={() => setExpanded(true)}
                      onDoubleClick={() => setExpanded(true)}
                    >
                      <SmartImage
                        src={getExpandedImage(selectedActivity)}
                        alt={`Artwork in ${selectedActivity?.styleInfo?.name || 'Transformed'} style`}
                        className="w-full h-full object-cover"
                        containerClassName="w-full h-full"
                        onLoad={() => {
                          console.log('✅ Details Modal Image loaded successfully:', getExpandedImage(selectedActivity));
                        }}
                        onError={(e) => {
                          console.error('❌ Details Modal Failed to load image:', getExpandedImage(selectedActivity));
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.parentElement.querySelector('.image-fallback');
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                      {/* Fallback when image fails to load */}
                      <div className="image-fallback hidden absolute inset-0 items-center justify-center bg-amber-50">
                        <div className="text-center">
                          <div className="text-6xl mb-4">🎨</div>
                          <p className="text-lg font-semibold text-amber-700">
                            {selectedActivity?.styleInfo?.name || 'Transformed'} Artwork
                          </p>
                          <p className="text-sm text-amber-600 mt-2">
                            Image loading...
                          </p>
                        </div>
                      </div>
                      <div className="absolute bottom-2 left-2 text-center text-amber-700 font-semibold bg-white/80 px-2 py-1 rounded">
                        {selectedActivity?.styleInfo?.name || 'Transformed'}
                      </div>
                      <div className="absolute top-2 right-2 bg-amber-500 text-white px-3 py-1 rounded-full text-sm font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        Double-click to expand!
                      </div>
                      <div className="absolute inset-0 bg-amber-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-2xl"></div>
                    </div>
                    <p className="text-sm text-amber-600 mt-2 text-center">
                      Double-click to learn more about {selectedActivity?.styleInfo?.name || 'this style'}!
                    </p>
                  </div>
                  </div>

                  {/* Style Information */}
                  {selectedActivity?.styleInfo && (
                    <div className="bg-amber-50 rounded-lg p-4 border-2 border-amber-200">
                      <h4 className="font-semibold text-amber-800 mb-2">About {selectedActivity.styleInfo.name}</h4>
                      <p className="text-amber-700 text-sm">
                        {selectedActivity.styleInfo.description || 'A beautiful art style transformation.'}
                      </p>
                    </div>
                  )}

                  {/* Tutorial Button - Bottom */}
                  <div className="text-center pt-6">
                      <button 
                        onClick={() => {
                        if (handleTutorialClick && selectedActivity) {
                          handleTutorialClick(selectedActivity);
                          setShowDetailsModal(false);
                        }
                        }}
                        className="bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 text-white px-8 py-4 rounded-full text-xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                        style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}
                      >
                      View {selectedActivity?.styleInfo?.name || 'Style'} Tutorial
                      </button>
                  </div>
                </div>


              </div>
            ) : selectedActivity.type === 'daily_activity' ? (
              <div className="p-6">
                <div className="space-y-6">
                  {/* Daily Activity Display */}
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                    {/* Activity Instructions */}
                    <div className="activity-instructions">
                      <h4 className="font-semibold text-amber-800 mb-3 text-center">Activity Details</h4>
                      <div className="bg-amber-50 rounded-lg p-4 border-2 border-amber-200">
                        <h5 className="font-semibold text-amber-800 mb-2">{selectedActivity.title || selectedActivity.activityTitle || `Daily Activity ${selectedActivity.activityId}`}</h5>
                        <p className="text-amber-700 text-sm mb-3">{selectedActivity.description || selectedActivity.activityDescription || "Daily creative activity for developing artistic thinking"}</p>
                        {selectedActivity.metadata && (
                          <div className="space-y-2">
                            {selectedActivity.metadata.prerequisites && (
                              <div>
                                <span className="font-medium text-amber-700">Prerequisites: </span>
                                <span className="text-amber-600 text-sm">{selectedActivity.metadata.prerequisites}</span>
                              </div>
                            )}
                            {selectedActivity.metadata.steps && (
                              <div>
                                <span className="font-medium text-amber-700">Steps: </span>
                                <span className="text-amber-600 text-sm">{selectedActivity.metadata.steps}</span>
                              </div>
                            )}
                            {selectedActivity.metadata.category && (
                              <div>
                                <span className="font-medium text-amber-700">Category: </span>
                                <span className="text-amber-600 text-sm">{selectedActivity.metadata.category}</span>
                              </div>
                            )}
                            {selectedActivity.metadata.difficulty && (
                              <div>
                                <span className="font-medium text-amber-700">Difficulty: </span>
                                <span className="text-amber-600 text-sm">{selectedActivity.metadata.difficulty}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* User's Artwork */}
                    <div className="user-artwork">
                      <h4 className="font-semibold text-amber-800 mb-3 text-center">Your Artwork</h4>
                      <div className="relative w-full h-[400px] rounded-2xl border-4 border-amber-200 shadow-lg overflow-hidden bg-white/90 backdrop-blur-sm">
                        <SmartImage
                          src={resolveImageUrl(selectedActivity.transformedImageUrl || selectedActivity.userArtworkUrl || selectedActivity.originalImageUrl || selectedActivity.activityPageUrl || '/static/media/create-art.png')}
                          alt="Your artwork"
                          className="w-full h-full object-cover"
                          containerClassName="w-full h-full"
                          onError={(e) => {
                            console.error('🖼️ Failed to load user artwork:', e.currentTarget.src);
                            e.currentTarget.style.display = 'none';
                            const fallback = e.currentTarget.parentElement.querySelector('.image-fallback');
                            if (fallback) fallback.style.display = 'flex';
                          }}
                        />
                        {/* Fallback when image fails to load */}
                        <div className="image-fallback hidden absolute inset-0 items-center justify-center bg-amber-50">
                          <div className="text-center">
                            <div className="text-6xl mb-4">🎨</div>
                            <p className="text-lg font-semibold text-amber-700">Your Artwork</p>
                            <p className="text-sm text-amber-600 mt-2">Image loading...</p>
                          </div>
                        </div>
                        <div className="absolute bottom-2 left-2 text-center text-amber-700 font-semibold bg-white/80 px-2 py-1 rounded">Your Artwork</div>
                      </div>
                    </div>
                  </div>

                  {/* Completion Status */}
                  {selectedActivity.isCompleted && (
                    <div className="bg-green-50 rounded-lg p-4 border-2 border-green-200">
                      <div className="flex items-center gap-2 text-green-800">
                        <FaCheckCircle size={20} />
                        <span className="font-semibold">Activity Completed!</span>
                      </div>
                      <p className="text-green-700 text-sm mt-1">
                        Completed on {formatActivityDate(selectedActivity, { includeTime: true, fallback: 'recently' })}
                      </p>
                    </div>
                  )}

                  {/* Artmosphere Feedback */}
                  {selectedActivity.feedback && (
                    <div className="bg-amber-50 rounded-lg p-4 border-2 border-amber-200">
                      <h4 className="font-semibold text-amber-800 mb-3">Artmosphere Feedback</h4>
                      
                      {/* Overall Message */}
                      {selectedActivity.feedback.overallMessage && (
                        <div className="mb-4 p-3 bg-white rounded-lg border border-amber-200">
                          <h5 className="font-medium text-amber-800 mb-2">Overall Message</h5>
                          <p className="text-amber-700 text-sm leading-relaxed">{selectedActivity.feedback.overallMessage}</p>
                        </div>
                      )}
                      
                      {/* Strengths */}
                      {selectedActivity.feedback.strengths && selectedActivity.feedback.strengths.length > 0 && (
                        <div className="mb-4 p-3 bg-white rounded-lg border border-amber-200">
                          <h5 className="font-medium text-amber-800 mb-2">Strengths</h5>
                          <ul className="list-disc list-inside text-amber-700 text-sm space-y-1">
                            {selectedActivity.feedback.strengths.map((strength, index) => (
                              <li key={index}>{strength}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Improvements */}
                      {selectedActivity.feedback.improvements && selectedActivity.feedback.improvements.length > 0 && (
                        <div className="mb-4 p-3 bg-white rounded-lg border border-amber-200">
                          <h5 className="font-medium text-amber-800 mb-2">Areas for Improvement</h5>
                          <ul className="list-disc list-inside text-amber-700 text-sm space-y-1">
                            {selectedActivity.feedback.improvements.map((improvement, index) => (
                              <li key={index}>{improvement}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Gentle Guidance (for off-topic activities) */}
                      {selectedActivity.feedback.gentleGuidance && (
                        <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <h5 className="font-medium text-blue-800 mb-2">Gentle Guidance</h5>
                          <p className="text-blue-700 text-sm leading-relaxed">{selectedActivity.feedback.gentleGuidance}</p>
                        </div>
                      )}
                      
                      {/* Suggestions (for off-topic activities) */}
                      {selectedActivity.feedback.suggestions && selectedActivity.feedback.suggestions.length > 0 && (
                        <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <h5 className="font-medium text-blue-800 mb-2">Suggestions</h5>
                          <ul className="list-disc list-inside text-blue-700 text-sm space-y-1">
                            {selectedActivity.feedback.suggestions.map((suggestion, index) => (
                              <li key={index}>{suggestion}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Raw feedback fallback */}
                      {!selectedActivity.feedback.overallMessage && !selectedActivity.feedback.strengths && (
                        <div className="p-3 bg-white rounded-lg border border-amber-200">
                          <p className="text-amber-700 text-sm">{typeof selectedActivity.feedback === 'string' ? selectedActivity.feedback : 'Feedback available'}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="flex flex-col lg:flex-row gap-4 sm:gap-6 lg:gap-8 items-start justify-center p-6">
                {/* Left: Artwork with glowing circles - Exact size and style */}
                <div className="relative w-full lg:w-[500px] h-[500px] bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg border-2 border-amber-200 overflow-hidden flex-shrink-0">
                  <img
                    src={resolveImageUrl(selectedActivity.originalImageUrl)}
                    alt={selectedActivity.title}
                    className="w-full h-full object-cover"
                  />
                  <button
                    className="absolute top-3 right-3 bg-white/80 rounded-full p-2 shadow hover:bg-white z-10"
                    onClick={() => setExpanded(true)}
                    title="Expand"
                  >
                    <FaExpandArrowsAlt size={16} />
                  </button>
                  <div className="absolute bottom-2 left-2 text-center text-amber-700 font-semibold bg-white/80 px-2 py-1 rounded">Your Artwork</div>
                  {selectedActivity.type === 'appreciation' && selectedActivity.aiPoints && selectedActivity.aiPoints.map((point, index) => {
                      if (!point || !point.point || typeof point.point.x !== 'number' || typeof point.point.y !== 'number') {
                        return null;
                      }
                      const x = Math.max(0.05, Math.min(0.95, point.point.x));
                      const y = Math.max(0.05, Math.min(0.95, point.point.y));
                      return (
                        <div
                          key={index}
                        className="absolute w-10 h-10 flex items-center justify-center rounded-full font-bold text-white text-sm shadow-xl ring-2 ring-white animate-pulse cursor-pointer hover:scale-125 hover:ring-4 transition-all duration-300 z-10 bg-gradient-to-br from-amber-500 to-amber-400 ring-white"
                        style={{ top: `${y * 100}%`, left: `${x * 100}%`, transform: 'translate(-50%, -50%)' }}
                        onClick={() => setVisibleBoxIndex(index)}
                          title={`Click to discover something magical about ${point.label || 'this area'}!`}
                        >
                          {index + 1}
                        </div>
                      );
                    })}
                  {selectedActivity.type === 'appreciation' && visibleBoxIndex !== null && selectedActivity.aiPoints && selectedActivity.aiPoints[visibleBoxIndex] && (
                    <div className="absolute top-4 left-4 right-4 bg-white/95 backdrop-blur-sm border-2 border-amber-200 rounded-2xl p-4 text-sm text-gray-800 shadow-xl z-50">
                        <div className="flex justify-between items-center">
                        <strong className="text-amber-700">{selectedActivity.aiPoints[visibleBoxIndex]?.label}</strong>
                        <button onClick={() => setVisibleBoxIndex(null)} className="text-amber-600 hover:text-amber-800 transition-colors">✖</button>
                        </div>
                      <p className="mt-2 text-gray-700">{selectedActivity.aiPoints[visibleBoxIndex]?.text}</p>
                      </div>
                  )}
                  </div>

                {/* Right: Mentor Tip, Did You Know, Technique Popup, Chat - Exact layout */}
                  <div className="w-full md:w-[400px] space-y-4 overflow-y-auto max-h-[60vh] pb-8">
                  {selectedActivity.type === 'appreciation' && selectedActivity.suggestion && (
                    <div className="bg-white/80 backdrop-blur-sm border-2 border-amber-200 rounded-2xl p-4 shadow-lg">
                      <button
                        className="art-btn-primary w-full font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
                        onClick={() => setShowTipBox(!showTipBox)}
                      >
                        Click for Artmosphere Mentor Tip
                      </button>
                      {showTipBox && (
                        <div className="mt-4 relative bg-white/90 backdrop-blur-sm text-amber-800 p-4 rounded-xl border-2 border-amber-200 shadow-lg">
                        <strong className="block text-lg mb-2">Mentor Tip:</strong>
                          <p className="text-gray-700">{selectedActivity.suggestion}</p>
                        <p className="text-sm text-amber-600 mt-3 italic border-t border-amber-100 pt-2">
                          Want to know more? Use the "Ask Your Mentor" chat box below for detailed feedback!
                        </p>
                      </div>
                      )}
                    </div>
                  )}
                  {selectedActivity.type === 'appreciation' && selectedActivity.techniques && selectedActivity.techniques.length > 0 && (
                    <div className="mt-8">
                      <button
                        onClick={() => setShowTechniques(!showTechniques)}
                        className="art-btn-primary w-full font-bold py-3 px-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-2"
                      >
                        <span>Did You Know?</span>
                      </button>
                      <AnimatePresence>
                        {showTechniques && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            transition={{ duration: 0.3 }}
                            className="mt-4 space-y-4 overflow-hidden"
                          >
                            {selectedActivity.techniques.map((tech, index) => (
                              <motion.div key={index} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.1 }} className="bg-white/80 backdrop-blur-sm border-2 border-amber-200 rounded-xl p-4 shadow-lg">
                              <div className="flex items-start gap-2">
                                <div className="w-full">
                                  <div className="flex justify-between items-center mb-1">
                                      <h4 className="text-amber-700 font-semibold">{tech.name || tech.title}</h4>
                                      <button onClick={() => { setSelectedTechnique(tech); setShowTechniquePopup(true); }} className="text-amber-600 hover:text-amber-800 text-sm font-medium">Learn More →</button>
                                            </div>
                                    <div className="mb-3 bg-amber-50 rounded-lg p-3 border border-amber-200">
                                      <h5 className="font-semibold text-amber-700 mb-2">About {tech.name || tech.title}</h5>
                                      <div className="text-gray-700"><p className="mb-2">{tech.description}</p></div>
                                      <button onClick={() => { setSelectedTechnique(tech); setShowTechniquePopup(true); }} className="text-amber-600 hover:text-amber-800 text-sm font-medium flex items-center gap-1 mt-3">Look Here →</button>
                                              </div>
                                              </div>
                                              </div>
                              </motion.div>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                                              </div>
                  )}
                  {selectedActivity.type !== 'appreciation' && (
                    <div className="bg-white/80 backdrop-blur-sm border-2 border-amber-200 rounded-2xl p-4 shadow-lg">
                      <h3 className="text-xl font-bold text-amber-800 mb-4">Activity Information</h3>
                      <div className="space-y-3">
                        {selectedActivity.content && (
                          <div>
                            <strong>Content:</strong>
                            <p className="text-gray-700 mt-1">{selectedActivity.content}</p>
                                              </div>
                        )}
                        {selectedActivity.story && (
                          <div>
                            <strong>Story:</strong>
                            <p className="text-gray-700 mt-1">{selectedActivity.story}</p>
                                            </div>
                        )}
                                          </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Ways of Seeing Modal */}
      <WaysOfSeeingModal
        isOpen={showWaysOfSeeing}
        onClose={closeWaysOfSeeing}
        originalArtwork={selectedOriginalArtwork}
        originalArtworkTitle={selectedOriginalArtworkTitle}
      />

      {/* Technique Details Popup */}
      {showTechniquePopup && selectedTechnique && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
          style={{ 
            zIndex: 10000,
            paddingTop: 'max(1rem, env(safe-area-inset-top, 0px))',
            paddingLeft: 'env(safe-area-inset-left, 0px)',
            paddingRight: 'env(safe-area-inset-right, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)'
          }}
          onClick={(e) => {
            // Close popup when clicking outside
            if (e.target === e.currentTarget) {
              setShowTechniquePopup(false);
            }
          }}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-lg p-6 max-w-2xl w-full overflow-y-scroll"
            style={{ 
              maxHeight: '90vh', 
              minHeight: '60vh',
              overflowY: 'scroll',
              scrollbarWidth: 'auto',
              msOverflowStyle: 'auto'
            }}
            onClick={(e) => {
              // Prevent clicks inside popup from closing it
              e.stopPropagation();
            }}
            onWheel={(e) => {
                                        // Prevent scroll events from bubbling to background
                                          e.stopPropagation();
            }}
            onTouchMove={(e) => {
              // Prevent touch scroll events from bubbling to background
                                          e.stopPropagation();
            }}
          >
            <div className="flex justify-between items-start mb-4 sticky top-0 bg-white pb-2 z-10">
              <h3 className="text-2xl font-bold text-amber-800">{selectedTechnique.name || selectedTechnique.title}</h3>
              <button
                onClick={() => setShowTechniquePopup(false)}
                className="text-gray-500 hover:text-gray-700 transition-colors text-xl font-bold"
              >
                ✕
                                    </button>
                                  </div>
            <div className="space-y-4 pb-4">
              <div className="bg-amber-50 p-4 rounded-lg">
                <h4 className="font-bold text-amber-800 mb-2">Did You Know?</h4>
                <p className="text-gray-700 leading-relaxed">{selectedTechnique.description}</p>
                                    </div>
              {selectedTechnique.explanation && (
                <div className="bg-amber-50 p-4 rounded-lg">
                  <h4 className="font-bold text-amber-800 mb-2">How the Artist Used This</h4>
                  <p className="text-gray-700 leading-relaxed">{selectedTechnique.explanation}</p>
                                            </div>
              )}
              {selectedTechnique.location && (
                <div className="bg-amber-50 p-4 rounded-lg">
                  <h4 className="font-bold text-amber-800 mb-2">Look Here!</h4>
                  <p className="text-gray-700 leading-relaxed">{selectedTechnique.location.replace('Look Here: ', '')}</p>
                                              </div>
              )}
              {selectedTechnique.example && (
                <div className="bg-amber-50 p-4 rounded-lg">
                  <h4 className="font-bold text-amber-800 mb-2">Famous Artist</h4>
                  <p className="text-gray-700 leading-relaxed">Did you know? Even famous artists like {selectedTechnique.example} have used this technique in their artworks!</p>
                                              </div>
              )}
                                              </div>
          </motion.div>
        </motion.div>
      )}

      {/* Expanded Artwork Modal - Desktop: overlay style, Mobile: fullscreen break-out */}
      {expanded && selectedActivity && typeof document !== 'undefined' && createPortal(
        <div 
          className={`mobile-expanded-modal fixed inset-0 flex items-center justify-center overflow-hidden ${isMobile ? 'mobile-fullscreen' : ''}`}
          style={{
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingLeft: 'env(safe-area-inset-left, 0px)',
            paddingRight: 'env(safe-area-inset-right, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)'
          }}
          style={{ 
            zIndex: isMobile ? 2147483647 : 9999, // Maximum z-index on mobile, normal on desktop
            position: 'fixed', 
            top: 0, 
            left: 0, 
            right: 0, 
            bottom: 0, 
            width: '100vw', 
            height: '100vh',
            maxHeight: '100vh',
            maxWidth: '100vw',
            display: 'flex',
            margin: 0,
            padding: isMobile ? 0 : '1rem', // No padding on mobile, padding on desktop
            backgroundColor: isMobile ? 'rgba(0, 0, 0, 0.95)' : 'transparent', // Transparent on desktop (overlay handles background)
            WebkitOverflowScrolling: 'touch',
            touchAction: isMobile ? 'none' : 'auto',
            overscrollBehavior: 'none',
            isolation: 'isolate', // Create new stacking context
            willChange: 'transform', // Optimize for mobile
            // Mobile-specific: Force break out of all containers
            ...(isMobile && {
              position: 'fixed',
              top: '0px',
              left: '0px',
              right: '0px',
              bottom: '0px',
              margin: '0px',
              padding: '0px',
              transform: 'none',
              transformOrigin: '0 0'
            })
          }}
          onTouchMove={(e) => {
            // Prevent body scroll on mobile when modal is open
            if (isMobile) {
              e.preventDefault();
            }
          }}
        >
          {console.log('🖼️ Expanded modal should be visible! URL:', getExpandedImage(selectedActivity))}
          <button
            className="absolute top-4 right-4 md:top-6 md:right-6 bg-white/90 rounded-full p-2 md:p-3 shadow hover:bg-white z-60"
            onClick={() => {
              setExpanded(false);
              setShowAppreciationModal(false); // Ensure appreciation modal stays closed
            }}
            title="Close"
          >
            <FaTimes size={24} className="md:w-7 md:h-7" />
          </button>
          
          {/* Expanded View Layout */}
          {selectedActivity?.type === 'reimagine' ? (
            <div className="bg-white/95 backdrop-blur-sm rounded-2xl p-6 max-w-7xl w-full max-h-[95vh] overflow-y-auto">
              {/* Modal Header */}
              <div className="flex justify-between items-center mb-6">
                <div className="text-center flex-1">
                  <h3 className="text-3xl font-bold text-amber-800 mb-2" style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}>
                    Create Your Own Amazing {selectedActivity?.styleInfo?.name || 'Style'} Masterpiece!
                  </h3>
                  <p className="text-lg text-amber-700" style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}>
                    Let's create your own masterpiece together!
                  </p>
                                              </div>
                <button 
                  onClick={() => setExpanded(false)}
                  className="text-amber-600 hover:text-amber-800 text-2xl font-bold ml-4"
                >
                  ×
                </button>
                                              </div>
              
              {/* Expanded Content Grid — right column top aligns with transformed image */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-8 xl:items-start">
                <h4 className="text-xl font-semibold text-amber-800 mb-4 flex items-center gap-2 xl:mb-0 xl:col-start-1 xl:row-start-1">
                  <span className="text-2xl">🎨</span>
                  Your Inspiration
                </h4>
                <div className="relative w-full h-[500px] rounded-2xl border-4 border-amber-200 shadow-lg overflow-hidden bg-white/90 backdrop-blur-sm xl:col-start-1 xl:row-start-2">
                  <SmartImage
                    src={getExpandedImage(selectedActivity)}
                    alt={`Artwork in ${selectedActivity?.styleInfo?.name || 'Style'} style`}
                    className="w-full h-full object-cover"
                    containerClassName="w-full h-full"
                    onLoad={() => console.log('🖼️ Expanded image loaded successfully!')}
                    onError={(e) => console.error('🖼️ Failed to load expanded image:', e.currentTarget.src)}
                  />
                  <div className="absolute bottom-3 left-3 text-center text-amber-700 font-semibold bg-white/90 px-3 py-2 rounded-lg shadow">
                    {selectedActivity?.styleInfo?.name || 'Style'} Style
                  </div>
                  <button className="absolute top-3 right-3 bg-white/80 hover:bg-white text-amber-700 p-2 rounded-full shadow-lg transition-all duration-300">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                  </button>
                </div>
                <div className="mt-4 text-center xl:col-start-1 xl:row-start-3 xl:mt-4">
                  {console.log('🔍 Button render (Location 2) - hasTutorial:', hasTutorial, 'styleInfo:', selectedActivity?.styleInfo?.name)}
                  <button 
                    onClick={() => {
                      console.log('🎯 Button clicked! (Location 2)');
                      console.log('🎯 selectedActivity:', selectedActivity);
                      console.log('🎯 Activity type:', selectedActivity?.type);
                      console.log('🎯 Activity styleInfo:', selectedActivity?.styleInfo);
                      console.log('🎯 hasTutorial state:', hasTutorial);
                      handleTutorialClick(selectedActivity);
                    }}
                    className="bg-gradient-to-r from-orange-500 to-amber-400 hover:from-orange-600 hover:to-amber-500 text-white px-8 py-4 rounded-full text-xl font-bold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105"
                    style={{ fontFamily: 'Baloo 2, Comic Sans MS, cursive' }}
                  >
                    {hasTutorial ? 
                      `View ${selectedActivity?.styleInfo?.name || 'Style'} Tutorial` : 
                      `Let's learn how to make artwork in ${selectedActivity?.styleInfo?.name || 'this style'}`
                    }
                  </button>
                </div>
                {/* Right: Art Form Information */}
                <div className="info-section space-y-6 xl:col-start-2 xl:row-start-2 xl:row-span-2 xl:self-stretch xl:min-h-0">
                  <h4 className="text-xl font-semibold text-amber-800 mb-4 flex items-center gap-2">
                    <span className="text-2xl">✨</span>
                    Let's Create Step by Step!
                  </h4>
                  
                  {/* Art Form Description */}
                  {(() => { 
                    const styleInfo = selectedActivity?.styleInfo || {};
                    const base = artStyles?.find(s => s.id === styleInfo.id) || {};
                    const mergedStyle = { ...base, ...styleInfo };
                    
                    return (
                  <div className="description-section">
                    <h4 className="text-xl font-semibold text-amber-800 mb-3 flex items-center gap-2">
                      About {mergedStyle?.name || 'Style'} Style
                    </h4>
                    <div className="bg-amber-50/80 backdrop-blur-sm rounded-xl p-6 border-2 border-amber-200 shadow-lg">
                      <p className="text-amber-800 leading-relaxed font-medium">
                        {mergedStyle?.description || 'This unique art style transforms your artwork with distinctive characteristics and techniques.'}
                            </p>
                          </div>
                  </div>
                    );
                  })()}

                  {/* Characteristics */}
                  {(() => { const styleInfo = selectedActivity?.styleInfo || {}; const base = artStyles?.find(s => s.id === styleInfo.id) || {}; const mergedStyle = { ...base, ...styleInfo }; return (
                  <div className="characteristics-section">
                    <h4 className="text-xl font-semibold text-amber-800 mb-3">Key Characteristics</h4>
                    <div className="flex flex-wrap gap-2">
                      {mergedStyle?.characteristics?.map((char, index) => (
                        <span key={index} className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                          {char}
                        </span>
                      )) || (
                        <span className="bg-amber-100 text-amber-800 px-3 py-1 rounded-full text-sm font-medium">
                          Unique color palette and composition
                        </span>
                        )}
                      </div>
                    </div>
                  ); })()}

                  {/* Famous Artists */}
                  {(() => { const styleInfo = selectedActivity?.styleInfo || {}; const base = artStyles?.find(s => s.id === styleInfo.id) || {}; const mergedStyle = { ...base, ...styleInfo }; return (mergedStyle?.famousArtists && mergedStyle.famousArtists.length > 0) ? (
                    <div className="artists-section">
                      <h4 className="text-xl font-semibold text-amber-800 mb-3">Famous Artists</h4>
                      <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                        <div className="flex flex-wrap gap-2">
                          {mergedStyle.famousArtists.map((artist, index) => (
                            <span 
                              key={index}
                              className="bg-white text-amber-700 px-3 py-1 rounded-lg text-sm font-medium border border-amber-200"
                            >
                              {artist}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ) : null; })()}
                  
                  {/* Time Period */}
                  {(() => { const styleInfo = selectedActivity?.styleInfo || {}; const base = artStyles?.find(s => s.id === styleInfo.id) || {}; const mergedStyle = { ...base, ...styleInfo }; return mergedStyle?.yearRange ? (
                    <div className="period-section">
                      <h4 className="text-xl font-semibold text-amber-800 mb-3">Time Period</h4>
                      <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
                        <p className="text-amber-800 font-semibold">
                          {mergedStyle.yearRange}
                        </p>
                      </div>
                    </div>
                  ) : null; })()}
                </div>
              </div>
            </div>
          ) : (() => {
            // Check if this is an art-history-appreciation activity
            const isArtHistoryAppreciation = selectedActivity?.type === "art-history-appreciation" || 
                                           selectedActivity?.metadata?.feature === 'art-history-appreciation' || 
                                           selectedActivity?.feature === 'art-history-appreciation';
            
            if (isArtHistoryAppreciation) {
              // For art-history-appreciation, render expanded view separately using createPortal
              // This matches ArtHistoryAppreciation.js exactly - render outside the modal structure
              return null; // Return null here, we'll render it separately below
            }
            
            // Original expanded modal for other activity types - Mobile-optimized fullscreen */
            return (
              <div 
                className={`relative flex items-center justify-center overflow-hidden ${isMobile ? 'w-screen h-screen' : 'max-w-[95vw] max-h-[90vh] rounded-2xl border-4 border-amber-200'} shadow-2xl ${isMobile ? 'bg-black' : 'bg-white'}`}
                style={{
                  width: isMobile ? '100vw' : 'auto',
                  height: isMobile ? '100vh' : 'auto',
                  maxWidth: isMobile ? '100vw' : '95vw',
                  maxHeight: isMobile ? '100vh' : '90vh',
                  margin: isMobile ? 0 : 'auto',
                  padding: 0,
                  position: 'relative'
                }}
              >
                <div 
                  className="relative flex items-center justify-center"
                  style={{
                    width: isMobile ? '100vw' : '100%',
                    height: isMobile ? '100vh' : '100%',
                    maxWidth: isMobile ? '100vw' : '100%',
                    maxHeight: isMobile ? '100vh' : '100%',
                    margin: 0,
                    padding: 0,
                    position: 'relative'
                  }}
                >
                  <img
                    src={getExpandedImage(selectedActivity)}
                    alt="Expanded Artwork"
                    className={`object-contain ${isMobile ? 'w-full h-full' : 'max-w-full max-h-full w-auto h-auto'}`}
                    style={{ 
                      background: 'transparent',
                      maxWidth: isMobile ? '100vw' : '95vw',
                      maxHeight: isMobile ? '100vh' : '90vh',
                      width: isMobile ? '100%' : 'auto',
                      height: isMobile ? '100%' : 'auto',
                      display: 'block',
                      objectFit: 'contain'
                    }}
                    onLoad={() => console.log('🖼️ Expanded image loaded successfully!')}
                    onError={(e) => console.error('🖼️ Failed to load expanded image:', e.target.src)}
                  />
                  
                  {/* Artmosphere Appreciation Points on Expanded Artwork */}
                  {(selectedActivity?.type === "appreciation" || selectedActivity?.type === "art-history-appreciation" || selectedActivity?.metadata?.feature === 'art-history-appreciation' || selectedActivity?.feature === 'art-history-appreciation') && selectedActivity?.aiPoints && selectedActivity.aiPoints.map((point, index) => {
                  if (!point || !point.point || typeof point.point.x !== 'number' || typeof point.point.y !== 'number') {
                    return null;
                  }

                  // Ensure coordinates are within bounds (0.1 to 0.9)
                  const x = Math.min(Math.max(point.point.x, 0.1), 0.9);
                  const y = Math.min(Math.max(point.point.y, 0.1), 0.9);

                  return (
                    <div
                      key={index}
                      className="absolute w-12 h-12 flex items-center justify-center rounded-full font-bold text-white text-lg bg-gradient-to-br from-amber-500 to-amber-400 shadow-xl ring-2 ring-white animate-pulse cursor-pointer hover:scale-125 hover:ring-4 transition-all duration-300 z-10"
                      style={{
                        top: `${y * 100}%`,
                        left: `${x * 100}%`,
                        transform: "translate(-50%, -50%)"
                      }}
                      onClick={() => setVisibleBoxIndex(index)}
                      title="Click to discover something magical!"
                    >
                      {index + 1}
                    </div>
                  );
                })}

                  {/* Artmosphere appreciation popup for selected circle in expanded view */}
                  {visibleBoxIndex !== null && selectedActivity?.aiPoints && selectedActivity.aiPoints[visibleBoxIndex] && (
                    <div className="absolute top-2 left-2 right-2 md:top-4 md:left-4 md:right-4 max-w-[calc(100%-1rem)] md:max-w-[calc(100%-2rem)] bg-white/95 backdrop-blur-sm border-2 border-amber-200 rounded-xl md:rounded-2xl p-3 md:p-4 text-xs md:text-sm text-gray-800 shadow-xl z-50">
                      <div className="flex justify-between items-center">
                        <strong className="text-amber-700 text-xs md:text-sm">{selectedActivity.aiPoints[visibleBoxIndex]?.label}</strong>
                        <button
                          onClick={() => setVisibleBoxIndex(null)} 
                          className="text-amber-600 hover:text-amber-800 transition-colors text-lg md:text-xl"
                        >
                          ✖
                        </button>
                      </div>
                      <p className="mt-2 text-gray-700 text-xs md:text-sm">{selectedActivity.aiPoints[visibleBoxIndex]?.text}</p>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
      </div>
      , typeof document !== 'undefined' ? document.body : null)}

      {/* Step-by-Step Learning Modal */}
      {showTutorialModal && tutorialStyleInfo && tutorialImage && (
        <>
          {console.log('🔍 ActivityDashboard: Rendering StepByStepLearning with:', {
                    artworkId: selectedActivity?.id,
                    styleId: tutorialStyleInfo?.id,
            hasTutorial: hasTutorial,
            hasExistingTutorialData: !!selectedActivity?.tutorialData,
            existingTutorialDataKeys: selectedActivity?.tutorialData ? Object.keys(selectedActivity.tutorialData) : [],
            selectedActivityKeys: selectedActivity ? Object.keys(selectedActivity) : [],
            fullSelectedActivity: selectedActivity,
            originalImageUrl: selectedActivity?.originalImageUrl,
            transformedImageUrl: selectedActivity?.transformedImageUrl
                  })}
        <StepByStepLearning
          transformedImage={tutorialImage}
          styleInfo={tutorialStyleInfo}
          originalImageUrl={selectedActivity?.originalImageUrl || tutorialImage}
                    artworkId={selectedActivity?.id || 'unknown'}
                    existingTutorialData={selectedActivity?.tutorialData}
          onClose={() => {
                      console.log('🔒 Closing tutorial modal');
            setShowTutorialModal(false);
            setTutorialStyleInfo(null);
            setTutorialImage(null);
            setTutorialKey(0);
            setExpanded(false);
            // Return to the same dashboard list view (no dark overlay/modal left behind).
            setShowDetailsModal(false);
            setSelectedActivity(null);
            // Force a small delay to ensure state is cleared
            setTimeout(() => {
              console.log('✅ Tutorial modal state cleared');
            }, 100);
          }}
          onBackToResults={() => {
                      console.log('🔒 Going back to results');
            setShowTutorialModal(false);
            setTutorialStyleInfo(null);
            setTutorialImage(null);
            setTutorialKey(0);
            setShowDetailsModal(false);
            setExpanded(false);
            setSelectedActivity(null);
          }}
          userAge={10}
          userId={auth.currentUser?.uid}
          forceGenerate={!hasTutorial}
                    hasTutorial={hasTutorial}
                    key={`tutorial-${tutorialStyleInfo?.id}-${tutorialKey}-${selectedActivity?.id}`}
        />
                </>
      )}

      {/* Expanded Modal - Duplicate (keeping for compatibility) - Fullscreen on mobile - Hide when main expanded modal is shown */}
      {expanded && selectedActivity && (
        <div className="fixed inset-0 z-[9999] bg-black bg-opacity-90 flex items-center justify-center overflow-hidden p-0 md:p-4" style={{ display: 'none' }}>
          <button
            onClick={() => setExpanded(false)}
            className="absolute top-4 right-4 md:top-6 md:right-6 bg-white/90 rounded-full p-2 md:p-3 shadow hover:bg-white z-60"
            title="Close"
          >
            <FaTimes size={24} className="md:w-7 md:h-7" />
          </button>
          <div className="flex flex-col items-center justify-center w-full h-full md:max-w-[95vw] md:max-h-[90vh]">
            <div className="relative w-full h-full flex items-center justify-center">
              <img
                src={getExpandedImage(selectedActivity)}
                alt={`${selectedActivity.styleInfo?.name || 'Transformed'} artwork`}
                className="w-full h-full md:max-w-full md:max-h-full md:w-auto md:h-auto object-contain md:rounded-2xl md:border-4 border-amber-400 shadow-2xl"
                style={{ background: 'transparent' }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Transformation Navigation Modal */}
      <TransformationNavigationModal
        isOpen={showTransformationNavigation}
        onClose={() => {
          setShowTransformationNavigation(false);
          setSelectedOriginalArtworkForNavigation(null);
          setSelectedOriginalArtworkTitleForNavigation('');
          setInitialActivityForNavigation(null);
        }}
        originalArtwork={selectedOriginalArtworkForNavigation}
        originalArtworkTitle={selectedOriginalArtworkTitleForNavigation}
        initialActivity={initialActivityForNavigation}
        onTutorialClick={(activity) => {
          // Close the navigation modal and open the tutorial
          setShowTransformationNavigation(false);
          setSelectedOriginalArtworkForNavigation(null);
          setSelectedOriginalArtworkTitleForNavigation('');
          setInitialActivityForNavigation(null);
          // Open tutorial for this activity
          handleTutorialClick(activity);
        }}
      />

      <UpgradeQuotaModal
        isOpen={showUpgradeModal}
        onClose={dismissUpgradeModal}
        feature={upgradeModalFeature}
        onUpgrade={() => { dismissUpgradeModal(); navigate('/membership'); }}
      />

      {/* Art Appreciation Modal — premium ARTmosphere shell */}
      {showAppreciationModal && selectedAppreciationActivity && !expanded && (
        <PremiumActivityModalShell
          open
          onClose={() => setShowAppreciationModal(false)}
          title="Art Appreciation"
          eyebrow="Magic Studio"
          subtitle="Art Appreciation & Tech Insights"
          dataActivityDashboard
          ariaLabelledBy="art-appreciation-modal-title"
        >
              <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6 lg:flex-row">
                {/* Left Side - Artwork - Below on mobile */}
                <div className="flex w-full flex-col items-center justify-center order-1 lg:w-1/2 lg:order-1">
                  {(() => {
                    const isArtHistoryAppreciation =
                      selectedAppreciationActivity?.type === 'art-history-appreciation' ||
                      selectedAppreciationActivity?.metadata?.feature === 'art-history-appreciation' ||
                      selectedAppreciationActivity?.feature === 'art-history-appreciation';
                    if (!isArtHistoryAppreciation) return null;
                    return (
                      <div className="mb-4 flex w-full items-center justify-between gap-3 rounded-2xl border border-[#F5D99B]/70 bg-gradient-to-br from-[#FFFDF9] to-[#FFF5E8] px-4 py-3 shadow-[0_6px_18px_rgba(120,72,20,0.06)]">
                        <h3 className={`text-sm font-semibold sm:text-base ${coLearningInkTitle}`}>Artwork Analysis</h3>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedActivity(selectedAppreciationActivity);
                            setExpanded(true);
                            setShowAppreciationModal(false);
                          }}
                          className="expand-artwork-button inline-flex h-9 w-9 items-center justify-center rounded-xl border border-[#E8C97A] bg-[#FFF9EF] text-[#5C4428] shadow-sm transition hover:border-[#F5CB5C] hover:bg-white hover:shadow-md"
                          title="Expand Artwork"
                        >
                          <FaExpandArrowsAlt className="h-4 w-4" />
                        </button>
                      </div>
                    );
                  })()}

                  <div className="relative w-full">
                    {(getExpandedImage(selectedAppreciationActivity) || selectedAppreciationActivity.originalImageUrl) && (
                      <div className="relative bg-white rounded-xl md:rounded-2xl shadow-2xl p-2 md:p-4" style={{ position: 'relative', overflow: 'visible' }}>
                        <div className="relative" style={{ position: 'relative', width: '100%', height: 'auto' }}>
                          <img
                            src={getExpandedImage(selectedAppreciationActivity) || resolveImageUrl(selectedAppreciationActivity.originalImageUrl)}
                            alt={selectedAppreciationActivity.title || "Artwork"}
                            className="w-full h-auto max-h-[60vh] md:max-h-none object-contain rounded-lg md:rounded-xl"
                            style={{ position: 'relative', display: 'block' }}
                          />
                          
                          {/* Analysis Points Overlay - Direct children like old implementation */}
                          {selectedAppreciationActivity.aiPoints && Array.isArray(selectedAppreciationActivity.aiPoints) && selectedAppreciationActivity.aiPoints.length > 0 && selectedAppreciationActivity.aiPoints.map((point, index) => {
                            if (!point || !point.point || typeof point.point.x !== 'number' || typeof point.point.y !== 'number') {
                              return null;
                            }
                            
                            // Ensure coordinates are within bounds (0.1 to 0.9) - matching old implementation
                            const x = Math.min(Math.max(point.point.x, 0.1), 0.9);
                            const y = Math.min(Math.max(point.point.y, 0.1), 0.9);
                            
                            console.log(`🔍 [HOTSPOTS] Rendering button ${index + 1} at x:${x * 100}%, y:${y * 100}%`);
                            
                            return (
                              <div
                                key={index}
                                className="absolute w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-full font-bold text-white text-xs md:text-sm bg-gradient-to-br from-amber-500 to-amber-400 shadow-xl ring-2 ring-white cursor-pointer hover:scale-125 hover:ring-4 transition-all duration-300 z-10"
                                style={{
                                  top: `${y * 100}%`,
                                  left: `${x * 100}%`,
                                  transform: 'translate(-50%, -50%)'
                                }}
                                onClick={() => setAppreciationVisibleBoxIndex(appreciationVisibleBoxIndex === index ? null : index)}
                                title={`Click to discover something magical about ${point.label || 'this area'}!`}
                              >
                                {index + 1}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Right Side - Analysis Panel */}
                <div className="order-2 w-full lg:w-1/2 lg:order-2">
                  <div className="max-h-[calc(100vh-12rem)] overflow-y-auto rounded-2xl border border-[#F5D99B]/60 bg-gradient-to-br from-[#FFFDF9] to-[#FFF5E8] p-4 shadow-[0_6px_18px_rgba(120,72,20,0.06)] md:max-h-[calc(100vh-8rem)] md:p-6 lg:sticky lg:top-24 lg:p-6">
                    {/* Dynamic Content Based on Selected Point */}
                    {appreciationVisibleBoxIndex !== null && selectedAppreciationActivity.aiPoints && selectedAppreciationActivity.aiPoints[appreciationVisibleBoxIndex] ? (
                      <div className="space-y-6">
                        <div className="border-b border-[#F5D99B]/50 pb-4">
                          <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0 flex-1 text-center">
                              <h2 className={`mb-2 font-serif text-xl font-semibold sm:text-2xl ${coLearningInkTitle}`}>
                                {selectedAppreciationActivity.aiPoints[appreciationVisibleBoxIndex].label}
                              </h2>
                              <div className="inline-flex items-center rounded-full border border-[#F5D99B]/80 bg-[#FFF9EF] px-3 py-1 text-sm font-medium text-[#92400E]">
                                Point {appreciationVisibleBoxIndex + 1} of {selectedAppreciationActivity.aiPoints.length}
                              </div>
                            </div>
                            <button
                              type="button"
                              onClick={() => setAppreciationVisibleBoxIndex(null)}
                              className="shrink-0 rounded-xl border border-[#E8C97A] bg-[#FFF9EF] px-3 py-2 text-sm font-medium text-[#5C4428] transition hover:bg-white"
                            >
                              Close
                            </button>
                          </div>
                        </div>

                        <div className="rounded-2xl border border-[#F5D99B]/70 bg-white/80 p-5 shadow-[0_4px_14px_rgba(120,72,20,0.05)]">
                          <h3 className={`mb-4 flex items-center text-lg font-semibold ${coLearningInkTitle}`}>
                            <span className="mr-3 flex h-8 w-8 items-center justify-center rounded-full border border-[#F5D99B] text-sm font-bold text-[#B45309]">
                              {appreciationVisibleBoxIndex + 1}
                            </span>
                            Did YOU KNOW?
                          </h3>
                          <p className={`mb-4 text-base leading-relaxed sm:text-lg ${coLearningInkBody}`}>
                            {selectedAppreciationActivity.aiPoints[appreciationVisibleBoxIndex].text}
                          </p>
                          {selectedAppreciationActivity.aiPoints[appreciationVisibleBoxIndex].improve?.length > 0 && (
                            <div className="rounded-xl border border-[#F5D99B]/50 bg-[#FFF9EF]/80 p-4">
                              <p className="mb-3 text-sm font-semibold uppercase tracking-wide text-[#9A7A55]">Artmosphere tips</p>
                              <ul className="space-y-2">
                                {selectedAppreciationActivity.aiPoints[appreciationVisibleBoxIndex].improve.map((suggestion, sIndex) => (
                                  <li key={sIndex} className={`flex items-start ${coLearningInkBody}`}>
                                    <span className="mr-3 mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-[#E8C97A] text-xs font-semibold text-[#5C4428]">
                                      {sIndex + 1}
                                    </span>
                                    <span className="text-base leading-relaxed">{suggestion}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <p className={`border-b border-[#F5D99B]/50 pb-4 text-center text-sm sm:text-base ${coLearningInkMuted}`}>
                          Tap the numbered markers on the artwork to explore each insight, or open the sections below.
                        </p>
                        
                        {/* Did YOU KNOW? */}
                        {(() => {
                          // Get points array (handle both aiPoints and appreciation)
                          let points = [];
                          if (selectedAppreciationActivity.aiPoints && Array.isArray(selectedAppreciationActivity.aiPoints) && selectedAppreciationActivity.aiPoints.length > 0) {
                            points = selectedAppreciationActivity.aiPoints;
                          } else if (selectedAppreciationActivity.appreciation && Array.isArray(selectedAppreciationActivity.appreciation) && selectedAppreciationActivity.appreciation.length > 0) {
                            points = selectedAppreciationActivity.appreciation.slice(0, 4).map((text, idx) => ({
                              text: typeof text === 'string' ? text : (text?.text || text?.label || ''),
                              improve: text?.improve || []
                            }));
                          }
                          
                          if (points.length === 0) return null;
                          
                          return (
                            <PremiumAccordion
                              title="Did YOU KNOW?"
                              isOpen={appreciationActiveSection === 'techniqueInsights'}
                              onToggle={() => toggleAppreciationSection('techniqueInsights')}
                            >
                              <div className="space-y-4">
                                {points.map((point, index) => (
                                  <div key={index} className="flex items-start gap-3">
                                    <div className="mt-0.5 flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-[#F5D99B] text-xs font-semibold text-[#B45309]">
                                      {index + 1}
                                    </div>
                                    <div className="min-w-0 flex-1">
                                      <p className={`mb-2 leading-relaxed ${coLearningInkBody}`}>{point.text || ''}</p>
                                      {point.improve?.length > 0 && (
                                        <div className="rounded-xl border border-[#F5D99B]/50 bg-[#FFF9EF]/80 p-3">
                                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[#9A7A55]">Artmosphere tips</p>
                                          <ul className="space-y-1">
                                            {point.improve.map((improvement, impIndex) => (
                                              <li key={impIndex} className={`flex items-start text-sm ${coLearningInkBody}`}>
                                                <span className="mr-2 text-[#B45309]">•</span>
                                                <span>{improvement}</span>
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </PremiumAccordion>
                          );
                        })()}
                        
                        {/* Did you know this technique looks like? */}
                        {selectedAppreciationActivity.artAnalysis?.techniques?.length > 0 && (
                          <PremiumAccordion
                            title="Did you know this technique looks like?"
                            isOpen={appreciationActiveSection === 'techniques'}
                            onToggle={() => toggleAppreciationSection('techniques')}
                          >
                            <div className="space-y-4">
                              {selectedAppreciationActivity.artAnalysis.techniques.map((technique, index) => (
                                <div key={index} className="rounded-xl border border-[#F5D99B]/40 bg-[#FFF9EF]/60 p-4">
                                  <h5 className={`mb-2 text-base font-semibold ${coLearningInkTitle}`}>{technique.name}</h5>
                                  <p className={`mb-2 ${coLearningInkBody}`}>{technique.description}</p>
                                  {technique.location && (
                                    <p className={`mt-2 text-xs ${coLearningInkMuted}`}>📍 {technique.location}</p>
                                  )}
                                  {technique.example && (
                                    <p className={`mt-1 text-xs ${coLearningInkMuted}`}>💡 Example: {technique.example}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </PremiumAccordion>
                        )}
                        
                        {/* Which Art movement this artwork could belong to? */}
                        {selectedAppreciationActivity.artAnalysis?.movements?.length > 0 && (
                          <PremiumAccordion
                            title="Which Art movement this artwork could belong to?"
                            isOpen={appreciationActiveSection === 'movements'}
                            onToggle={() => toggleAppreciationSection('movements')}
                          >
                            <div className="space-y-4">
                              {selectedAppreciationActivity.artAnalysis.movements.map((movement, index) => (
                                <div key={index} className="rounded-xl border border-[#F5D99B]/40 bg-[#FFF9EF]/60 p-4">
                                  <h5 className={`mb-2 text-base font-semibold ${coLearningInkTitle}`}>{movement.name}</h5>
                                  <p className={`mb-2 ${coLearningInkBody}`}>{movement.description}</p>
                                  {movement.influence && (
                                    <p className={`rounded-lg border border-[#F5D99B]/30 bg-white/80 p-2 text-sm italic ${coLearningInkBody}`}>{movement.influence}</p>
                                  )}
                                  {movement.famousExample && (
                                    <p className={`mt-2 text-xs ${coLearningInkMuted}`}>💡 Famous example: {movement.famousExample}</p>
                                  )}
                                </div>
                              ))}
                            </div>
                          </PremiumAccordion>
                        )}
                        
                        <PremiumAccordion
                          title="Key Concepts"
                          isOpen={appreciationActiveSection === 'concepts'}
                          onToggle={() => toggleAppreciationSection('concepts')}
                        >
                          <div className="space-y-4">
                            {(selectedAppreciationActivity.concepts?.length
                              ? selectedAppreciationActivity.concepts
                              : [
                                  { title: 'Contrast', text: 'Contrast helps define focus and depth.' },
                                  { title: 'Edges', text: 'Soft vs hard edges shape realism and mood.' },
                                  { title: 'Color Harmony', text: 'Related hues create a cohesive feel.' },
                                ]
                            ).map((concept, index) => (
                              <div key={index} className="rounded-xl border border-[#F5D99B]/40 bg-[#FFF9EF]/60 p-4">
                                <h5 className={`mb-2 text-base font-semibold ${coLearningInkTitle}`}>{concept.title}</h5>
                                <p className={coLearningInkBody}>{concept.text}</p>
                              </div>
                            ))}
                          </div>
                        </PremiumAccordion>
                      </div>
                    )}
                  </div>
                </div>
              </div>
        </PremiumActivityModalShell>
      )}

      {/* Separate Expanded View for Art History Appreciation */}
      {expanded && typeof document !== 'undefined' && (() => {
        // Use selectedActivity first, fallback to selectedAppreciationActivity for art history learning activities
        const activity = selectedActivity || selectedAppreciationActivity;
        
        if (!activity) return null;
        
        const isArtHistoryAppreciation = activity?.type === "art-history-appreciation" || 
                                       activity?.metadata?.feature === 'art-history-appreciation' || 
                                       activity?.feature === 'art-history-appreciation';
        
        if (isArtHistoryAppreciation) {
          return createPortal(
            <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-4">
              <div className="relative w-[95vw] h-[85vh] max-w-6xl max-h-[85vh] bg-white rounded-2xl shadow-2xl border-4 border-amber-200 overflow-hidden">
                {/* Close Button for Pop-out - Top Right */}
                <button
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log('[ActivityDashboard] Close expanded art-history view');
                    setExpanded(false);
                  }}
                  className="absolute top-4 right-4 z-50 p-3 bg-white hover:bg-gray-50 rounded-xl shadow-xl transition-all duration-200 border-2 border-gray-300 hover:border-amber-400"
                  title="Close Expanded View"
                  style={{ minWidth: '44px', minHeight: '44px' }}
                >
                  <svg className="w-5 h-5 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                  </svg>
                </button>
                
                <div className="h-full flex items-center justify-center p-4">
                  <div className="w-full h-full flex items-center justify-center">
                    <ArtAnalysisHotspots
                      imageUrl={getExpandedImage(activity) || activity?.originalImageUrl || activity?.imageUrl}
                      analysisText={(() => {
                        // Handle both 'appreciation' (array of strings) and 'aiPoints' (array of objects)
                        if (activity?.appreciation && Array.isArray(activity.appreciation)) {
                          return activity.appreciation.slice(0, 4);
                        } else if (activity?.aiPoints && Array.isArray(activity.aiPoints)) {
                          return activity.aiPoints.slice(0, 4).map(p => (typeof p === 'string' ? p : (p?.text || p?.label || p?.appreciation || '')));
                        }
                        return [];
                      })()}
                      title={activity?.title || "Art History Artwork"}
                      enableFullscreen={false}
                      layout="columnLeft"
                      concepts={activity?.concepts || []}
                      hideHotspots={false}
                    />
                  </div>
                </div>
              </div>
            </div>,
            document.body
          );
        }
        return null;
      })()}
    </>
  );
}
